import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const rawBaseQuery = fetchBaseQuery({
  // The default query prefix for the API is `/api`
  baseUrl: "/api",
  prepareHeaders: (headers, { getState }) => {
    // Add the access token to API requests when available
    const accessToken = getState().auth.accessToken;
    if (accessToken) {
      headers.set("authorization", `Bearer ${accessToken}`);
    }

    return headers;
  },
});

// If any authenticated request comes back 401 (e.g. the admin row was deleted
// out from under the session), clear local credentials and bounce to /login.
const baseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result?.error?.status === 401) {
    try {
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("u");
      window.localStorage.removeItem("a");
    } catch (_e) { /* localStorage unavailable; ignore */ }
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  }
  return result;
};

// Drop empty/false query params so they fall back to the server's defaults.
const cleanParams = (params) => {
  const clean = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) clean[k] = v;
  });
  return clean;
};

// Define the single API slice object for the `api` service
export const api = createApi({
  // The cache reducer expects to be added at `state.api` (the default)
  reducerPath: "api",

  baseQuery,

  // The `endpoints` represent operations and requests for this service.
  // The baseline endpoints are implemented here that are common to all
  // features. The app features extend this service with injectEndpoints().
  endpoints: (builder) => ({
    // Connectivity health check with api service
    getHealth: builder.query({
      query: () => "/health",
    }),

    // Create User from UI
    createUser: builder.mutation({
      query: (p) => ({
        url: "/create_web_user",
        method: "POST",
        params: {'username': p.username, 'password': p.password, 'age': p.age}
      })
    }),

    // Login request
    login: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(credentials).toString(),
      }),
    }),

    loginCodeValid: builder.mutation({
      query: (credentials) => ({
        url: "/login_code_valid",
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(credentials).toString(),
      }),
    }),

    adminRole: builder.mutation({
      query: (p) => ({
        url: "/admin_role",
        method: "POST",
        params:{'uuid': p.uuid}
      }),
    }),

    adminLogin: builder.mutation({
      query: (credentials) => ({
        url: "/login_admin",
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(credentials).toString(),
      }),
    }),

    adminLoginMs: builder.mutation({
      query: (body) => ({
        url: "/login_admin_ms",
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
      }),
    }),

    enable2fa: builder.mutation({
      query: (p) => ({
        url: "/enable_2fa",
        method: "POST",
        params: p
      }),
    }),

    check2faCode: builder.mutation({
      query: (p) => ({
        url: "/check_2fa_code",
        method: "POST",
        params: p
      }),
    }),

    contestImages: builder.query({
      query: (p) => ({
        url: "/get_contest_images",
        params:{'contest': p.contest}
      })
    }),

    liveStreamModelCounts: builder.query({
      query: () => "/live_cams_model_streams_for_ui",
    }),

    liveStreams: builder.query({
      query: () => "/live_stream_history",
    }),

    runSingleAml: builder.mutation({
      query: (p) => ({
        url: "/run_single_aml",
        method: "POST",
        params: {
          'run_type': p.run_type ,
          'account_type': p.account_type ,
          'account_id': p.account_id , 
          'first_name': p.first_name ,
          'last_name': p.last_name ,
          'business_name': p.business_name ,
          'country': p.country ,
          'admin_uuid': p.admin_uuid ,
          'admin_note': p.admin_note  ,
          'client_id': p.client_id  
        }
      }),
    }),

    postAutoAML: builder.mutation({
      query: (p) => ({
        url: "/post_auto_aml",
        method: "POST",
        body: p
      }),
    }),

    getPastAMLRuns: builder.query({
      query: () => "/past_aml_runs",
    }),

    startAccountUpdate: builder.mutation({
      query: (p) => ({
        url: "/run_account_updates",
        method: "POST",
        params: {
          'account_type': p.account_type,
          'status_flag': p.status_flag ,
          'admin_uuid': p.admin_uuid ,
          'admin_note': p.admin_note ,
        }
      }),
    }),

    sendEmail: builder.mutation({
      query: (p) => ({
        url: "/send_email",
        method: "POST",
        params: {'recipient':p.recipient},
      })
    }),

    captureContract: builder.mutation({
      query: (p) => ({
        url: "/capture-contract",
        method: "POST",
        body: p,
      })
    }),

    captureContractFromHtml: builder.mutation({
      query: (p) => ({
        url: "/capture-contract-from-html",
        method: "POST",
        body: p,
      })
    }),

    uploadS3: builder.mutation({
      query: (p) => ({
        url: "/s3-upload-internal-image",
        method: "POST",
        params: {'img_type':p.img_type, 
          'description': p.description, 
          'category': p.category, 
          'account_id':p.account_id, 
          'account_type':p.account_type, 
          'original_filename':p.original_filename},
        body: p.body,
        formData: true,
      })
    }),

    uploadKYBS3: builder.mutation({
      query: (p) => ({
        url: "/s3-upload-url-kyb",
        method: "POST",
        params: {'img_type':p.img_type, 
          'description': p.description, 
          'category': p.category,
          'full_path': p.full_path, 
          'filename':p.filename},
        body: p.body,
        formData: true,
      })
    }),

    kybFormSubmission: builder.mutation({
      query: (p) => ({
        url: "/kyb_form_submission",
        method: "POST",
        body: p 
      })
    }),

    deleteImage: builder.mutation({
      query: (p) => ({
        url: "/delete_image",
        method: "POST",
        params: {'image_id': p.image_id} 
      })
    }),

    editImage: builder.mutation({
      query: (p) => ({
        url: "/edit_image",
        method: "POST",
        params: {'image_id': p.image_id, "title": p.title, "description": p.description} 
      })
    }),
    
    // voting
    castVote: builder.mutation({
      query: (p) => ({
        url: "/vote",
        method: "POST",
        params: {'image_id': p.image_id, 'uuid': p.uuid}
      })
    }),

    removeVote: builder.mutation({
      query: (p) => ({
        url: "/remove_vote",
        method: "POST",
        params: {'image_id': p.image_id, 'uuid': p.uuid}
      })
    }),

    countUserVotes: builder.query({
      query: (p) => ({
        url: "/get_user_votes",
        params: {'uuid': p.uuid, 'contest': p.contest}
      })
    }),

    checkCode: builder.mutation({
      query: (p) => ({
        url: "/check_code",
        method: "POST",
        params: {'recipient': p.recipient, 'code': p.code}
      })
    }),

    getConfig: builder.query({
      query: (p) => ({
        url: "/get_config",
        params: {'name': p.name}
      })
    }),

    // App-wide admin config flags (Super Admin → Config). GET is public (the
    // login page reads it pre-auth); PUT is ADMIN-only.
    // Super Admin -> Firm <-> FSC Office mapping. tagTypes are inert app-wide,
    // so the page refetch()es after each mutation rather than relying on
    // invalidation.
    // Super Admin -> Work Access (office classification + per-user scopes).
    // Tech -> Bloom Accounts. Bloom's own data is cached server-side for six
    // hours; refetch() re-reads it (~3s, 85 concurrent calls).
    // Exec -> Bloom Board. Server-side cached for 30 minutes; refetch() re-reads
    // every meeting (~5s, 170 concurrent calls).
    // Super Admin -> FSC Sync. The trigger returns as soon as the task is
    // queued; the page polls the status while a run is in flight.
    getFscSync: builder.query({
      query: () => ({ url: "/admin/fsc-sync" }),
    }),
    triggerFscSync: builder.mutation({
      query: (p) => ({ url: "/admin/fsc-sync", method: "POST", body: { live: !!p.live } }),
    }),
    // Super Admin -> Orion Sync. Same shape as FSC above: the trigger returns
    // once the task is queued and the page polls while a run is in flight.
    // `nightly_enabled` governs the 07:00 UTC beat schedule only — the trigger
    // below works whether it is on or off.
    getOrionSync: builder.query({
      query: () => ({ url: "/admin/orion-sync" }),
    }),
    triggerOrionSync: builder.mutation({
      query: (p) => ({ url: "/admin/orion-sync", method: "POST", body: { live: !!p.live } }),
    }),
    // Find transaction types Orion has that `orion_transaction_types` does not.
    // Expensive on purpose — it reads a whole month with no type filter,
    // because Orion has no type catalogue endpoint — so it is a button and
    // never a schedule. Shares the sync's run record and its one-at-a-time
    // guard, hence the same trigger-then-poll shape.
    triggerOrionTypeCensus: builder.mutation({
      query: (p = {}) => ({
        url: "/admin/orion-type-census", method: "POST",
        body: { live: !!p.live, start: p.start || null, end: p.end || null },
      }),
    }),
    setOrionSyncEnabled: builder.mutation({
      query: (p) => ({ url: "/admin/orion-sync/enabled", method: "PUT",
                       body: { enabled: !!p.enabled } }),
    }),
    // Super Admin -> CRM Migration. The three stages of the Salentica->FSC
    // document pipeline. Same trigger-then-poll shape as the two syncs above;
    // the transcript is a separate lazy fetch because a run's captured stdout is
    // tens of kilobytes and the status is polled every few seconds.
    // Super Admin -> CRM Sync: refresh a firm's Work -> CRM book from Redtail.
    // The trigger queues a Celery task; the page polls the status while a
    // refresh is in flight.
    getCrmSync: builder.query({
      query: () => ({ url: "/admin/crm-sync" }),
    }),
    runCrmSync: builder.mutation({
      query: (p) => ({ url: "/admin/crm-sync/run", method: "POST", body: { source: p.source, mode: p.mode } }),
    }),
    // Match every firm's CRM contacts to EXISTING Salesforce households (reads
    // Salesforce, writes only the hub's crosswalk).
    runCrmMatch: builder.mutation({
      query: () => ({ url: "/admin/crm-sync/match", method: "POST" }),
    }),
    // SmartOffice list exports for a file-based firm (contacts and/or policies),
    // recognized server-side by their column headings.
    uploadCrmExport: builder.mutation({
      query: ({ source, files, update = false }) => {
        const form = new FormData();
        form.append("source", source);
        // update: match rows on their SmartOffice id instead of replacing the list.
        form.append("update", update ? "true" : "false");
        files.forEach((f) => form.append("files", f));
        return { url: "/admin/crm-sync/upload", method: "POST", body: form };
      },
    }),
    // Add / edit a firm on CRM Sync (crm_sources), and the firm + FSC office
    // dropdowns for that form.
    getCrmSyncOptions: builder.query({
      query: () => ({ url: "/admin/crm-sync/options" }),
    }),
    saveCrmSource: builder.mutation({
      query: (body) => ({ url: "/admin/crm-sync/sources", method: "POST", body }),
    }),
    getCrmMigration: builder.query({
      query: () => ({ url: "/admin/crm-migration" }),
    }),
    runCrmMigrationStep: builder.mutation({
      query: (p) => ({ url: "/admin/crm-migration/run", method: "POST",
                       body: { step: p.step, live: !!p.live } }),
    }),
    getCrmMigrationLog: builder.query({
      query: (runId) => ({ url: `/admin/crm-migration/runs/${runId}/log` }),
    }),
    // Super Admin -> Non-Clients. The hand-maintained half of "who is a client"
    // on the Meetings page; the merged-firm colleagues come from the directory
    // automatically and never appear here. Same convention as the admin pages
    // above: tags are inert, so the page refetch()es after each mutation.
    getNonClients: builder.query({
      query: () => ({ url: "/admin/non-clients" }),
    }),
    addNonClient: builder.mutation({
      query: (p) => ({ url: "/admin/non-clients", method: "POST",
                       body: { value: p.value, kind: p.kind,
                               label: p.label || null, reason: p.reason || null } }),
    }),
    deleteNonClient: builder.mutation({
      query: (p) => ({ url: `/admin/non-clients/${p.id}`, method: "DELETE" }),
    }),
    getBloomBoard: builder.query({
      query: (p = {}) => ({ url: `/exec/bloom-board${p.refresh ? '?refresh=true' : ''}` }),
    }),
    getBloomUsers: builder.query({
      query: (p = {}) => ({ url: `/tech/bloom-users${p.refresh ? '?refresh=true' : ''}` }),
    }),
    pinBloomUser: builder.mutation({
      query: (p) => ({ url: "/tech/bloom-users/pin", method: "POST",
                       body: { directory_id: p.directory_id, bloom_user_id: p.bloom_user_id ?? null } }),
    }),
    getWorkScopes: builder.query({
      query: () => ({ url: "/admin/work-scopes" }),
    }),
    setOfficeKind: builder.mutation({
      query: (p) => ({ url: "/admin/work-scopes/office-kind", method: "POST",
                       body: { fsc_id: p.fsc_id, kind: p.kind ?? null } }),
    }),
    setUserScope: builder.mutation({
      query: (p) => ({ url: "/admin/work-scopes/user", method: "POST",
                       body: { admin_uuid: p.admin_uuid, mode: p.mode ?? null,
                               office_ids: p.office_ids || [] } }),
    }),
    // The person's complete set of Work roles (areas + WORK_ADMIN) after the change.
    setUserWorkRoles: builder.mutation({
      query: (p) => ({ url: "/admin/work-scopes/roles", method: "POST",
                       body: { admin_uuid: p.admin_uuid, roles: p.roles || [] } }),
    }),
    getFirmOffices: builder.query({
      query: () => ({ url: "/admin/firm-offices" }),
    }),
    mapFirmOffice: builder.mutation({
      query: (p) => ({ url: "/admin/firm-offices/map", method: "POST",
                       body: { firm_id: p.firm_id, fsc_office_id: p.fsc_office_id ?? null } }),
    }),
    setFirmDisplayName: builder.mutation({
      query: (p) => ({ url: "/admin/firm-offices/display-name", method: "POST",
                       body: { firm_id: p.firm_id, source: p.source } }),
    }),
    getAdminConfig: builder.query({
      query: () => ({ url: "/admin_config" }),
      providesTags: ["AdminConfig"],
    }),
    setAdminConfig: builder.mutation({
      query: (p) => ({ url: "/admin_config", method: "PUT", body: { name: p.name, value: p.value } }),
      invalidatesTags: ["AdminConfig"],
    }),

    // Recipients emailed when a hub employee requests more Claude usage
    // (Super Admin → Config). ADMIN-only, both read and write.
    getEmployeeUsageNotify: builder.query({
      query: () => ({ url: "/admin_config/employee_usage_notify" }),
      providesTags: ["EmployeeUsageNotify"],
    }),
    setEmployeeUsageNotify: builder.mutation({
      query: (p) => ({ url: "/admin_config/employee_usage_notify", method: "PUT", body: { emails: p.emails } }),
      invalidatesTags: ["EmployeeUsageNotify"],
    }),

    // Firm-wide shared mailboxes offered in everyone's Your Day mailbox menu
    // (Super Admin → Config). ADMIN-only on read as well as write, unlike the
    // flags above: GET /admin_config is public for the login page, and internal
    // mailbox addresses don't belong in an unauthenticated response.
    getSharedMailboxes: builder.query({
      query: () => ({ url: "/admin_config/shared_mailboxes" }),
      providesTags: ["SharedMailboxes"],
    }),
    setSharedMailboxes: builder.mutation({
      query: (p) => ({ url: "/admin_config/shared_mailboxes", method: "PUT", body: { mailboxes: p.mailboxes } }),
      invalidatesTags: ["SharedMailboxes"],
    }),

    // Super Admin → Document Access Log. Server-side filtered + paginated —
    // the table can grow large, so unlike most admin pages here this isn't a
    // fetch-everything-then-filter-client-side query.
    searchDocumentAccessLog: builder.query({
      query: (p = {}) => {
        const params = new URLSearchParams();
        ["q", "action", "actor_type", "client_id", "firm_id", "date_from", "date_to", "limit", "offset"]
          .forEach((k) => { if (p[k] !== undefined && p[k] !== null && p[k] !== '') params.append(k, p[k]); });
        return { url: `/admin/document_access_log?${params.toString()}` };
      },
    }),

    // Super Admin → Sign-in Attempts. Client portal + Entrepreneur portal
    // sign-in, code and password-reset attempts. Server-side filtered + paged.
    searchLoginAttempts: builder.query({
      query: (p = {}) => {
        const params = new URLSearchParams();
        ["q", "flow", "kind", "outcome", "date_from", "date_to", "limit", "offset"]
          .forEach((k) => { if (p[k] !== undefined && p[k] !== null && p[k] !== '') params.append(k, p[k]); });
        return { url: `/admin/login_attempts?${params.toString()}` };
      },
    }),

    // Super Admin → Celery Jobs. Registered beat schedule (instant, no broker
    // round-trip) + live worker/task status (best-effort, can be slow/empty if
    // the broker or workers are unreachable) — polled so the page stays live.
    getCelerySchedule: builder.query({
      query: () => "/admin/celery/schedule",
    }),
    getCeleryWorkers: builder.query({
      query: () => "/admin/celery/workers",
    }),

    // DocuSign setup helpers (ADMIN-only; run from Super Admin → Config so the
    // session token is attached — hitting these endpoints directly 401s).
    getDocusignStatus: builder.query({
      query: () => ({ url: "/docusign/status" }),
    }),
    getDocusignConsentUrl: builder.query({
      query: () => ({ url: "/docusign/consent_url" }),
    }),
    getDocusignUserinfo: builder.query({
      query: () => ({ url: "/docusign/userinfo" }),
    }),
    docusignWarmup: builder.query({
      query: (p) => ({ url: "/docusign/warmup", params: { calls: (p && p.calls) || 20 } }),
    }),
    listDocusignTemplates: builder.query({
      query: (p) => ({ url: "/docusign/templates", params: (p && p.search) ? { search: p.search } : undefined }),
    }),
    inspectDocusignTemplate: builder.query({
      query: (p) => ({ url: `/docusign/templates/${(p && p.templateId) || 'default'}/inspect` }),
    }),

    getKYB: builder.query({
      query: () => ({
        url: "/get_kyb",
      })
    }),

    getKYBImages: builder.mutation({
      query: (p) => ({
        url: "/get_kyb_images",
        method: "POST",
        params: {'kyb_display': p.kyb_display}
      })
    }),

    getKYBResultByAccountAndType: builder.mutation({
      query: (p) => ({
        url: "/get_kyb_result_by_account_and_type",
        method: "POST",
        params: {'account_id': p.account_id, 'account_type': p.account_type}
      })
    }),

    

    getImages: builder.mutation({
      query: (p) => ({
        url: "/get_images",
        method: "POST",
        params: {'account_id': p.account_id, 'account_type': p.account_type}
      })
    }),

    getAllPartners: builder.query({
      query: () => ({
        url: "/get_all_partners",
      })
    }),

    getVerifyAccountsByType: builder.query({
      query: (p) => ({
        url: "/type_verify_accounts",
        params: {'account_type': p.account_type}
      })
    }),

    updatePartner: builder.mutation({
      query: (p) => ({
        url: "/update_partner_approval",
        method: "POST",
        params: {'kyb': p.kyb, 'approved': p.approved, 'note': p.note, 'deny': p.deny}
      })
    }),

    getAllMemberStats: builder.query({
      query: () => ({
        url: "/get_all_member_stats",
      })
    }),

    getAllMembersFor: builder.mutation({
      query: (p) => ({
        url: "/get_all_members_for",
        method: "POST",
        params: {'run_type': p.run_type}
      })
    }),

    getVerifyAccountCounts: builder.query({
      query: () => ({
        url: "/account_counts",
      })
    }),

    //** DEPRECATED! - use editVerify **//
    updateVerifyAccountStatus: builder.mutation({
      query: (p) => ({
        url: "/update_verify_account_status",
        method: "POST",
        params: {'account_type': p.account_type, 'account_id':p.account_id, 
          'overall_status': p.overall_status, 'overall_notes': p.overall_notes,
          'overall_status_by': p.overall_status_by}
      })
    }),

    editVerifyStatus: builder.mutation({
      query: (p) => ({
        url: "/edit_verify_status",
        method: "POST",
        params: {'client_id': p.client_id, 'scan_ref': p.scan_ref,
          'override_status': p.override_status, 'type': p.type, 'idenfy_id': p.idenfy_id,
          'admin_user': p.admin_user, 'admin_comment': p.admin_comment
        }
      })
    }),

    search: builder.mutation({
      query: (p) => ({
        url: "/search",
        method: "POST",
        params: {'search_string': p.search_string, 
          'search_type': p.search_type,
           'is_single_account': (p.is_single_account || 0)
          }
      })
    }),

    getStudioCounts: builder.query({
      query: () => ({
        url: "/studio_counts",
      })
    }),


    getStatusHistory: builder.mutation({
      query: (p) => ({
        url: "/get_all_kyc_history",
        method: "POST",
        params: {'account': p.account, 'company_id': p.company_id}
      })
    }),

    getIdenfyResponse: builder.query({
      query: (p) => ({
        url: "/get_idenfy_response",
        params: {'table': p.table, 'id': p.id}
      })
    }),

    getKYCSents: builder.mutation({
      query: (p) => ({
        url: "/get_kyc_sents",
        method: "POST",
        params: p
      })
    }),

    postKYCSent: builder.mutation({
      query: (p) => ({
        url: "/post_kyc_sent",
        method: "POST",
        params: p
      })
    }),

    uploadExcel: builder.mutation({
      query: ({file, accountType}) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('account_type', accountType);
        return {
          url: '/upload_bulk_aml/',
          method: 'POST',
          body: formData,
        };
      },
    }),

    uploadDacExcel: builder.mutation({
      query: (formData) => ({
        url: '/upload_dac/',
        method: 'POST',
        body: formData,
        responseHandler: async (response) => {
          // Check if it's an Excel file
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('spreadsheet')) {
            return response.blob();
          }
          // Otherwise it's probably an error (JSON)
          return response.json();
        },
      }),
    }),

    getAMLIndividualHistory: builder.query({
      query: (p) => ({
        url: "/aml_individual_history",
        params: {'account_type': p.account_type, 
          'account_id': p.account_id
        }
      })
    }),

    getAMLBusinessHistory: builder.query({
      query: (p) => ({
        url: "/aml_business_history",
        params: {'account_type': p.account_type, 'account_id': p.account_id}
      })
    }),

    getFailedAccounts: builder.mutation({
      query: (p) => ({
        url: "/failed_accounts",
        method: "POST",
        params: {'account_type': p.account_type, 'status_flag': p.status_flag}
      })
    }),


    launchKYStart: builder.mutation({
      query: (p) => ({
        url: "/launch_ky",
        method: "POST",
        params: p
      })
    }),

    launchKYFromInside: builder.mutation({
      query: (p) => ({
        url: "/launch_ky_from_inside",
        method: "POST",
        params: {'client_id': p.client_id, 'override_verify': p.override_verify}
      })
    }),

    getPresignedURL: builder.mutation({
      query: (p) => ({
        url: "/get_kyb_presigned",
        method: "POST",
        params: {'bucket_name': p.bucket_name, 'object_name': p.object_name}
      })
    }),


    getAllNotes: builder.query({
      query: (p) => ({
        url: "/get_all_notes",
        params: {'account_type': p.account_type, 'account_id': p.account_id}
      })
    }),

    getMentionableUsers: builder.query({
      query: (search = '') => ({
        url: `/mentionable${search ? `?search=${encodeURIComponent(search)}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['MentionableUsers'],
    }),

    addNote: builder.mutation({
      query: (p) => ({
        url: "/add_note",
        method: "POST",
        params: {
          'client_id': p.client_id,
          'account_type': p.account_type,
          'account_id': p.account_id,
          'note': p.note,
          'admin_uuid': p.admin_uuid,
          'image_id': p.image_id,
          'gpid': p.gpid,
          'account_name': p.account_name
        }
      })
    }),

    editNote: builder.mutation({
      query: (p) => ({
        url: "/edit_note",
        method: "POST",
        params: {
          'note_id': p.note_id,
          'account_type': p.account_type,
          'account_id': p.account_id,
          'note': p.note,
          'admin_uuid': p.admin_uuid
        }
      })
    }),

    runPost: builder.mutation({
      query: (p) => ({
        url: "/" + p.url,
        method: "POST",
        params: p
      }) 
    }), 

    runPostWithBody: builder.mutation({
      query: (p) => ({
        url: "/" + p.url,
        method: "POST",
        params: p.params,
        body: p.body,
        formData: true,
      }) 
    }), 


    runGet: builder.query({
      query: (p) => ({
        url: "/" + p.url,
        params: p
      })
    }), 

    getKYBResultsByPaymentStatus: builder.query({
      query: (p) => ({
        url: "/get_kyb_results_by_payment_status",
        params: {
          ...(p?.pay_status && { pay_status: p.pay_status }),
          ...(p?.assigned_to && { assigned_to: p.assigned_to }),
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size }),
          ...(p?.kyb_status && { kyb_status: p.kyb_status }),
          ...(p?.kyc_status && { kyc_status: p.kyc_status }),
          ...(p?.due_diligence_status && { due_diligence_status: p.due_diligence_status }),
          ...(p?.account_type && { account_type: p.account_type }),
          ...(p?.submission_source && { submission_source: p.submission_source })
        }
      })
    }),

    getIDVResultsWithSummary: builder.query({
      query: (p) => ({
        url: "/get_idv_results_with_summary",
        params: {
          ...(p?.account_id && { account_id: p.account_id }),
          ...(p?.account_type && { account_type: p.account_type }),
          ...(p?.status && { status: p.status }),
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size })
        }
      })
    }),

    getConfirmIDSummary: builder.query({
      query: () => ({
        url: "/get_confirmid_summary"
      })
    }),

    getConfirmIDDailyCounts: builder.query({
      query: (p) => ({
        url: "/get_confirmid_daily_counts",
        params: {
          ...(p?.days && { days: p.days })
        }
      })
    }),

    getConfirmIDImages: builder.query({
      query: (p) => ({
        url: "/get_confirmid_images",
        params: {
          model_id: p.model_id,
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size })
        }
      })
    }),

    getRekognitionSummary: builder.query({
      query: () => ({
        url: "/get_rekognition_summary"
      })
    }),

    getRekognitionDailyCounts: builder.query({
      query: (p) => ({
        url: "/get_rekognition_daily_counts",
        params: {
          ...(p?.days && { days: p.days })
        }
      })
    }),
    
    updateProfileAssignment: builder.mutation({
      query: (p) => ({
        url: "/update_profile_assignment",
        method: "POST",
        params: {
          'account_type': p.account_type,
          'account_id': p.account_id,
          'assigned_to': p.assigned_to
        }
      })
    }),
    
    updateUserAssignment: builder.mutation({
      query: (p) => ({
        url: "/update_user_assignment",
        method: "POST",
        params: {
          'account_type': p.account_type,
          'account_id': p.account_id,
          'admin_uuid': p.admin_uuid,
          'account_name': p.account_name
        }
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Account', id: `${arg.account_type}:${arg.account_id}` }
      ]
    }),

    updateCompanyName: builder.mutation({
      query: (p) => ({
        url: "/update_company_name",
        method: "POST",
        params: {
          'account_id': p.account_id,
          'account_type': p.account_type,
          'company_name': p.company_name
        }
      }),
      invalidatesTags: ['UserDashboard', 'KYBResults']
    }),

    getAdminUsers: builder.query({
      query: () => ({
        url: "/admin_users"
      })
    }),

    setAdminRoles: builder.mutation({
      query: (p) => ({
        url: "/set_admin_roles",
        method: "POST",
        body: {
          uuid: p.uuid,
          roles: p.roles,
        }
      })
    }),

    setAdminStatus: builder.mutation({
      query: (p) => ({
        url: "/set_admin_status",
        method: "POST",
        body: {
          uuid: p.uuid,
          status: p.status,
        }
      })
    }),

    sendKYCEmail: builder.mutation({
      query: (p) => ({
        url: "/send_kyc_email",
        method: "POST",
        body: p
      })
    }),

    sendManualKYCEmail: builder.mutation({
      query: (p) => ({
        url: "/send_manual_kyc_email",
        method: "POST",
        body: p
      })
    }),

    sendManualKYBEmail: builder.mutation({
      query: (p) => ({
        url: "/send_manual_kyb_email",
        method: "POST",
        body: p
      })
    }),

    expiredIdenfyKYC: builder.query({
      query: () => ({
        url: "/expired_kyc_idenfy",
        method: "GET"
      })
    }),

    getExpiredKYBDocuments: builder.query({
      query: () => ({
        url: "/get_expired_kyb_documents",
        method: "GET"
      })
    }),

    resetExpiredKYBDocuments: builder.mutation({
      query: (p) => ({
        url: "/reset_expired_kyb_documents",
        method: "POST",
        params: p
      })
    }),

    getUserDashboard: builder.query({
      query: () => ({
        url: "/user_dashboard",
      }),
      providesTags: ['UserDashboard'],
      refetchOnMountOrArgChange: true
    }),

    postPaxumData: builder.mutation({
      query: (p) => ({
        url: "/post_paxum_data",
        method: "POST",
        params: p
      })
    }),

    getCountryPaymentsConfig: builder.query({
      query: (p) => ({
        url: "/select_country_payments_config",
        method: "GET",
        params: p
      })
    }),

    deleteCountryPaymentConfig: builder.mutation({
      query: (p) => ({
        url: "/delete_country_payments_config",
        method: "DELETE",
        params: p
      })
    }),

    postCountryPaymentConfig: builder.mutation({
      query: (p) => ({
        url: "/insert_country_payment_config",
        method: "POST",
        body: p
      })
    }),

    postEditCountryPaymentConfig: builder.mutation({
      query: (p) => ({
        url: "edit_country_payment_config",
        method: "POST",
        body: p
      })
    }),

    getBannedCountryPaymentsConfig: builder.query({
      query: (p) => ({
        url: "/select_banned_country_payments_config",
        method: "GET",
        params: p
      })
    }),

    getCtoPMaps: builder.query({
      query: () => ({
        url: "/get_ctop_maps",
        method: "GET",
      })
    }),

    getCountryPaymentsConfigHistory: builder.query({
      query: (p) => ({
        url: "/get_country_payments_config_history",
        method: "GET",
        params: p
      })
    }),

    getDAC7Token: builder.query({
      query: (p) => ({
        url: "/get_dac7_token",
        method: "GET",
        params: p
      })
    }),

    checkDAC7Token: builder.query({
      query: (p) => ({
        url: "/check_dac7_token",
        method: "GET",
        params: p
      })
    }),

    getDAC7Data: builder.query({
      query: (p) => ({
        url: "/get_dac7_data",
        method: "GET",
        params: p
      })
    }),

    postDAC7Submission: builder.mutation({
      query: (p) => ({
        url: "/insert_dac7_submission",
        method: "POST",
        body: p
      })
    }),

    postAvailablePaymentConfig: builder.mutation({
      query: (p) => ({
        url: "/add_available_payment_config",
        method: "POST",
        body: p
      })
    }),
    
    getAvailablePaymentConfigs: builder.query({
      query: (p) => ({
        url: "/get_available_payment_configs",
        method: "GET",
        params: p
      })
    }),

    sendDAC7Email: builder.mutation({
      query: (p) => ({
        url: "/send_dac7_message",
        method: "POST",
        body: p
      })
    }),

    getDAC7Activity: builder.query({
      query: (p) => ({
        url: "/get_dac7_activity",
        params: {
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size }),
          ...(p?.status && { status: p.status }),
          ...(p?.year && { year: p.year }),
          ...(p?.studio_id && { studio_id: p.studio_id }),
          ...(p?.export && { export: p.export })
        }
      })
    }),

    getAdBuys: builder.query({
      query: (p) => ({
        url: "/get_adbuys",
        method: "GET",
        params: p
      })
    }),

    syncPaymentMMI: builder.query({
      query: (p) => ({
        url: "/sync_payment_to_mmi",
        method: "GET",
        params: p
      })
    }),

    getKYBSubmissionJSON: builder.query({
      query: (p) => ({
        url: "/getKYB_json",
        method: "GET",
        params: p
      })
    }),

    runTest1: builder.query({
      query: (p) => ({
        url: "/run_test_1",
        method: "GET",
        params: p
      })
    }),

    getReminders: builder.query({
      query: () => ({
        url: "/get_reminders",
        method: "GET"
      })
    }),

    insertReminder: builder.mutation({
      query: (p) => ({
        url: "/insert_reminder",
        method: "POST",
        body: p
      })
    }),

    deleteReminder: builder.mutation({
      query: (p) => ({
        url: "/delete_reminder",
        method: "DELETE",
        params: { id: p.id }
      })
    }),

    // ── Tech Audit ────────────────────────────────────────────────────────────

    loadTechAudit: builder.query({
      query: () => "/tech_audit_load",
    }),

    upsertTechCategory: builder.mutation({
      query: (body) => ({ url: "/tech_audit_upsert_category", method: "POST", body }),
    }),

    upsertTechEntry: builder.mutation({
      query: (body) => ({ url: "/tech_audit_upsert_entry", method: "POST", body }),
    }),

    addTechTask: builder.mutation({
      query: (body) => ({ url: "/tech_audit_add_task", method: "POST", body }),
    }),

    updateTechTask: builder.mutation({
      query: (body) => ({ url: "/tech_audit_update_task", method: "PUT", body }),
    }),

    deleteTechTask: builder.mutation({
      query: (p) => ({ url: "/tech_audit_delete_task", method: "DELETE", params: { id: p.id } }),
    }),

    deleteTechCategory: builder.mutation({
      query: (p) => ({ url: "/tech_audit_delete_category", method: "DELETE", params: { category_key: p.category_key } }),
    }),

    // ── Claude Enterprise Access Onboarding ───────────────────────────────────

    loadClaudeAccessStatus: builder.query({
      query: () => "/claude_access_status",
    }),

    confirmClaudeAccess: builder.mutation({
      query: (body) => ({ url: "/claude_access_confirm", method: "POST", body }),
    }),

    uploadClaudeCert: builder.mutation({
      query: (p) => ({
        url: "/claude_access_upload_cert",
        method: "POST",
        body: p.body,
        formData: true,
      }),
    }),

    listClaudeSubmissions: builder.query({
      query: () => "/claude_access_submissions",
    }),

    listUnmatchedClaudeAccounts: builder.query({
      query: () => "/claude_access_unmatched_accounts",
    }),

    listB2BClaudeActivations: builder.query({
      query: () => "/b2b_claude_activations",
    }),

    updateClaudeSubmissionStatus: builder.mutation({
      query: (body) => ({ url: "/claude_access_submission_status", method: "PUT", body }),
    }),

    getClaudeCertificateUrl: builder.mutation({
      query: (p) => ({ url: "/claude_access_certificate_url", method: "GET", params: { id: p.id } }),
    }),

    // ── Finance: Annuity statements ───────────────────────────────────────────

    listAnnuityStatements: builder.query({
      query: () => "/annuity_statements",
    }),

    listAnnuityPolicies: builder.query({
      query: () => "/annuity_policies",
    }),

    uploadAnnuityStatement: builder.mutation({
      query: (p) => ({
        url: "/annuity_upload",
        method: "POST",
        body: p.body,
        formData: true,
      }),
    }),

    getAnnuityStatementUrl: builder.mutation({
      query: (p) => ({ url: "/annuity_statement_url", method: "GET", params: { id: p.id } }),
    }),

    deleteAnnuityStatement: builder.mutation({
      query: (p) => ({ url: "/annuity_statement", method: "DELETE", params: { id: p.id } }),
    }),

    getAllianzStatus: builder.query({
      query: () => "/annuity_allianz_status",
    }),

    syncAllianz: builder.mutation({
      query: (p) => ({ url: "/annuity_allianz_sync", method: "POST", body: (p && p.body) || {} }),
    }),

    // ── HR Job Openings ───────────────────────────────────────────────────────
    // The list response is { openings, is_hr, is_manager, can_edit }. The same
    // endpoint serves the HR management page (sees everything + salary) and the
    // read-only board (filtered by visibility, no salary) — the backend decides.

    // Pass { manage: true } for the HR management view (every posting, incl.
    // inactive/filled, plus salary). Default is the public board view.
    listJobOpenings: builder.query({
      query: (arg) => ({ url: "/job_openings", params: (arg && arg.manage) ? { manage: true } : undefined }),
    }),

    createJobOpening: builder.mutation({
      query: (p) => ({ url: "/job_openings", method: "POST", body: p.body }),
    }),

    updateJobOpening: builder.mutation({
      query: (p) => ({ url: "/job_openings", method: "PUT", params: { id: p.id }, body: p.body }),
    }),

    deleteJobOpening: builder.mutation({
      query: (p) => ({ url: "/job_openings", method: "DELETE", params: { id: p.id } }),
    }),

    uploadJobOpeningJd: builder.mutation({
      query: (p) => ({
        url: "/job_openings/jd_upload",
        method: "POST",
        params: { id: p.id },
        body: p.body,
        formData: true,
      }),
    }),

    getJobOpeningJdUrl: builder.mutation({
      query: (p) => ({ url: "/job_openings/jd_url", method: "GET", params: { id: p.id } }),
    }),

    deleteJobOpeningJd: builder.mutation({
      query: (p) => ({ url: "/job_openings/jd", method: "DELETE", params: { id: p.id } }),
    }),

    uploadJobOpeningVideo: builder.mutation({
      query: (p) => ({
        url: "/job_openings/video_upload",
        method: "POST",
        params: { id: p.id },
        body: p.body,
        formData: true,
      }),
    }),

    getJobOpeningVideoUrl: builder.mutation({
      query: (p) => ({ url: "/job_openings/video_url", method: "GET", params: { id: p.id } }),
    }),

    deleteJobOpeningVideo: builder.mutation({
      query: (p) => ({ url: "/job_openings/video", method: "DELETE", params: { id: p.id } }),
    }),

    // HR-configured notification recipient lists. `notify_type` is
    // 'application' (auto-sent on submit) or 'posting' (manually triggered).
    listJobNotifyRecipients: builder.query({
      query: (arg) => ({
        url: "/job_openings/notify_recipients",
        params: (arg && arg.notify_type) ? { notify_type: arg.notify_type } : undefined,
      }),
      providesTags: ['JobNotifyRecipients'],
    }),

    addJobNotifyRecipient: builder.mutation({
      query: (body) => ({ url: "/job_openings/notify_recipients", method: "POST", body }),
      invalidatesTags: ['JobNotifyRecipients'],
    }),

    deleteJobNotifyRecipient: builder.mutation({
      query: (p) => ({ url: "/job_openings/notify_recipients", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['JobNotifyRecipients'],
    }),

    // Manually send the "new posting added" communication for one posting.
    notifyNewJobPosting: builder.mutation({
      query: (p) => ({ url: "/job_openings/notify_new_posting", method: "POST", params: { id: p.id } }),
    }),

    // ── Entrepreneur (B2B firm setup) — employee side, ENTREPRENEUR role ──────
    listEntrepreneurFirms: builder.query({
      query: () => "/entrepreneur/firms",
    }),
    createEntrepreneurFirm: builder.mutation({
      query: (body) => ({ url: "/entrepreneur/firms", method: "POST", body }),
    }),
    listEntrepreneurUsers: builder.query({
      query: (arg) => ({
        url: "/entrepreneur/users",
        params: (arg && arg.firm_id) ? { firm_id: arg.firm_id } : undefined,
      }),
    }),
    createEntrepreneurUser: builder.mutation({
      query: (body) => ({ url: "/entrepreneur/users", method: "POST", body }),
    }),
    deactivateEntrepreneurUser: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/users/${p.uuid}`, method: "DELETE" }),
    }),
    setEntrepreneurUserRole: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/users/${p.uuid}/role`, method: "PUT", body: { role: p.role } }),
    }),
    resetEntrepreneurUserPassword: builder.mutation({
      // Issues a fresh temp password for an existing B2B user — same idea as
      // the client portal's "Reset login". `send_email` optionally emails it.
      query: (p) => ({
        url: `/entrepreneur/users/${p.uuid}/reset_password`,
        method: "POST",
        params: { send_email: !!p.send_email },
      }),
    }),
    bulkUploadEntrepreneurUsers: builder.mutation({
      // body is a FormData with `file` (.xlsx or .csv) and optional `send_email`
      // and `dry_run` ("true" = report what would happen, write nothing).
      query: (body) => ({ url: "/entrepreneur/users/bulk", method: "POST", body, formData: true }),
    }),
    // Portal invites: members who have never signed in and have no recorded
    // invite, grouped by firm. Sending issues each a fresh temp password.
    listPendingPortalInvites: builder.query({
      query: () => "/entrepreneur/invites/pending",
    }),
    sendPortalInvites: builder.mutation({
      query: (firmIds) => ({ url: "/entrepreneur/invites/send", method: "POST", body: { firm_ids: firmIds } }),
    }),

    // ── Entrepreneur Claude-reseller: firm applications + seat provisioning ──
    listEntrepreneurApplications: builder.query({
      query: () => "/entrepreneur/applications",
    }),
    approveEntrepreneurFirm: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/firms/${p.firm_id}/approve`, method: "POST" }),
    }),
    rejectEntrepreneurFirm: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/firms/${p.firm_id}/reject`, method: "POST" }),
    }),
    verifyEntrepreneurDomain: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/firms/${p.firm_id}/domain/verify`, method: "POST" }),
    }),
    listEntrepreneurProvisionQueue: builder.query({
      query: (arg) => ({
        url: "/entrepreneur/provision-queue",
        params: (arg && (arg.firm_id || arg.include_closed))
          ? { ...(arg.firm_id ? { firm_id: arg.firm_id } : {}), ...(arg.include_closed ? { include_closed: true } : {}) }
          : undefined,
      }),
    }),
    approveEntrepreneurProvision: builder.mutation({
      query: (p) => ({
        url: `/entrepreneur/users/${p.uuid}/provision/approve`, method: "POST",
        body: {
          prorated_cents: p.prorated_cents || 0,
          tax_cents: p.tax_cents || 0,
          note: p.note || null,
          no_charge: !!p.no_charge,
        },
      }),
    }),
    getEntrepreneurFeePreview: builder.query({
      query: (p) => ({
        url: "/entrepreneur/billing/fee_preview",
        params: { prorated_cents: p.prorated_cents, tax_cents: p.tax_cents || 0 },
      }),
    }),
    listEntrepreneurActivationInvoices: builder.query({
      query: (arg) => ({
        url: "/entrepreneur/activation-invoices",
        params: (arg && arg.firm_id) ? { firm_id: arg.firm_id } : undefined,
      }),
    }),
    rejectEntrepreneurProvision: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/users/${p.uuid}/provision/reject`, method: "POST" }),
    }),
    // Hub membership and a Claude seat are separate: these open / withdraw a
    // seat request for an existing hub member on the firm's behalf.
    requestEntrepreneurClaudeSeat: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/users/${p.uuid}/claude_seat`, method: "POST" }),
    }),
    withdrawEntrepreneurClaudeSeat: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/users/${p.uuid}/claude_seat`, method: "DELETE" }),
    }),
    getEntrepreneurClaudeCertUrl: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/users/${p.uuid}/claude_cert_url`, method: "GET" }),
    }),
    listEntrepreneurInvoices: builder.query({
      query: (arg) => ({
        url: "/entrepreneur/invoices",
        params: (arg && arg.firm_id) ? { firm_id: arg.firm_id } : undefined,
      }),
    }),
    runEntrepreneurBilling: builder.mutation({
      query: (p) => ({
        url: `/entrepreneur/firms/${p.firm_id}/billing/run`,
        method: "POST",
        params: p.month ? { month: p.month } : undefined,
      }),
    }),
    // Charge Usage page: per-firm, per-closed-month usage vs paid, and a
    // confirmed charge of one row's outstanding balance.
    getEntrepreneurUsageCharges: builder.query({
      query: (p) => ({ url: "/entrepreneur/billing/usage_charges", params: { months: p?.months || 6 } }),
    }),
    chargeEntrepreneurUsage: builder.mutation({
      query: (p) => ({
        url: "/entrepreneur/billing/usage_charges/charge", method: "POST",
        // months: [{ month: 'YYYY-MM', expected_remaining_cents }] - one
        // Stripe charge covers them all.
        body: { firm_id: p.firm_id, months: p.months },
      }),
    }),
    syncEntrepreneurStripeCustomers: builder.mutation({
      query: () => ({ url: "/entrepreneur/billing/stripe_customers/sync", method: "POST" }),
    }),
    getEntrepreneurFirmBillingSummary: builder.query({
      query: (p) => `/entrepreneur/firms/${p.firm_id}/billing_summary`,
    }),
    getEntrepreneurFirmBillingStatus: builder.query({
      query: (p) => `/entrepreneur/firms/${p.firm_id}/billing_status`,
      providesTags: (result, error, p) => [{ type: "FirmBillingStatus", id: p.firm_id }],
    }),
    getEntrepreneurFirmSetupIntent: builder.query({
      query: (p) => `/entrepreneur/firms/${p.firm_id}/billing/setup_intent`,
    }),
    setEntrepreneurFirmPaymentMethod: builder.mutation({
      query: (p) => ({
        url: `/entrepreneur/firms/${p.firm_id}/billing/payment_method`, method: "POST",
        body: { setup_intent_id: p.setup_intent_id },
      }),
      invalidatesTags: (result, error, p) => [{ type: "FirmBillingStatus", id: p.firm_id }],
    }),
    removeEntrepreneurFirmPaymentMethod: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/firms/${p.firm_id}/billing/payment_method`, method: "DELETE" }),
      invalidatesTags: (result, error, p) => [{ type: "FirmBillingStatus", id: p.firm_id }],
    }),
    refundEntrepreneurInvoice: builder.mutation({
      query: (p) => ({
        url: "/entrepreneur/invoices/refund", method: "POST",
        body: { kind: p.kind, invoice_id: p.invoice_id, amount_cents: p.amount_cents ?? null, reason: p.reason || null },
      }),
    }),
    getEntrepreneurAnnualSeatFee: builder.query({
      query: () => "/entrepreneur/billing/annual_seat_fee",
      providesTags: ["AnnualSeatFee"],
    }),
    setEntrepreneurAnnualSeatFee: builder.mutation({
      query: (p) => ({ url: "/entrepreneur/billing/annual_seat_fee", method: "PUT", body: { cents: p.cents } }),
      invalidatesTags: ["AnnualSeatFee"],
    }),
    getEntrepreneurMaxUserLimit: builder.query({
      query: () => "/entrepreneur/billing/max_user_limit",
      providesTags: ["MaxUserLimit"],
    }),
    setEntrepreneurMaxUserLimit: builder.mutation({
      query: (p) => ({ url: "/entrepreneur/billing/max_user_limit", method: "PUT", body: { cents: p.cents } }),
      invalidatesTags: ["MaxUserLimit"],
    }),
    searchEntrepreneurActionsLog: builder.query({
      query: (p = {}) => {
        const params = new URLSearchParams();
        ["q", "action", "actor_type", "firm_id", "date_from", "date_to", "limit", "offset"]
          .forEach((k) => { if (p[k] !== undefined && p[k] !== null && p[k] !== '') params.append(k, p[k]); });
        return { url: `/entrepreneur/actions-log?${params.toString()}` };
      },
    }),
    // "Log in as" a B2B firm user (Prosperity ADMIN+ENTREPRENEUR only). Returns
    // a scoped B2B session token; the caller stores it under the B2B token key.
    impersonateEntrepreneurUser: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/users/${p.uuid}/impersonate`, method: "POST" }),
    }),
    // Follow-up notification recipients (Entrepreneur > Configuration; ADMIN+ENTREPRENEUR).
    getEntrepreneurNotifications: builder.query({
      query: () => "/entrepreneur/notifications",
    }),
    setEntrepreneurNotifications: builder.mutation({
      query: (p) => ({ url: "/entrepreneur/notifications", method: "PUT", body: { emails: p.emails } }),
    }),
    // Salesforce FSC training schedule (Entrepreneur > Configuration; ADMIN+ENTREPRENEUR).
    getFscTrainingConfig: builder.query({
      query: () => "/entrepreneur/fsc-training/config",
    }),
    setFscTrainingConfig: builder.mutation({
      query: (p) => ({ url: "/entrepreneur/fsc-training/config", method: "PUT", body: p }),
    }),
    listFscTrainingSignups: builder.query({
      query: () => "/entrepreneur/fsc-training/signups",
    }),

    // Firm-facing announcements shown on every B2B member's dashboard
    // ("Save the Date" + important information). Any ENTREPRENEUR staffer can
    // manage them. No providesTags/invalidatesTags: this app never declares
    // `tagTypes`, so tags are inert — the pages refetch() explicitly instead.
    listB2BAnnouncements: builder.query({
      query: () => "/entrepreneur/announcements",
    }),
    createB2BAnnouncement: builder.mutation({
      query: (body) => ({ url: "/entrepreneur/announcements", method: "POST", body }),
    }),
    updateB2BAnnouncement: builder.mutation({
      query: (body) => ({ url: "/entrepreneur/announcements", method: "PUT", body }),
    }),
    deleteB2BAnnouncement: builder.mutation({
      query: (p) => ({ url: "/entrepreneur/announcements", method: "DELETE", params: { id: p.id } }),
    }),

    // Transitioning firms - the stage flip and the transition dashboard editor
    // (Entrepreneur -> Transitions). Pages refetch() explicitly; tags are inert.
    listTransitions: builder.query({
      query: () => "/entrepreneur/transitions",
    }),
    setFirmStage: builder.mutation({
      query: (p) => ({ url: `/entrepreneur/firms/${p.firm_id}/stage`, method: "PUT", body: { stage: p.stage } }),
    }),
    getTransition: builder.query({
      query: (p) => `/entrepreneur/transitions/${p.firm_id}`,
    }),
    updateTransitionPlan: builder.mutation({
      query: ({ firm_id, ...body }) => ({ url: `/entrepreneur/transitions/${firm_id}`, method: "PUT", body }),
    }),
    // `collection` is 'areas' | 'tasks' | 'entries'; `id` absent = create.
    // (Not `kind` - an entry's body carries its own `kind`.)
    saveTransitionItem: builder.mutation({
      query: ({ firm_id, collection, id, ...body }) => ({
        url: `/entrepreneur/transitions/${firm_id}/${collection}${id ? `/${id}` : ""}`,
        method: id ? "PUT" : "POST",
        body,
      }),
    }),
    deleteTransitionItem: builder.mutation({
      query: ({ firm_id, collection, id }) => ({ url: `/entrepreneur/transitions/${firm_id}/${collection}/${id}`, method: "DELETE" }),
    }),
    reorderTransitionItems: builder.mutation({
      query: ({ firm_id, what, ids }) => ({ url: `/entrepreneur/transitions/${firm_id}/reorder`, method: "POST", body: { what, ids } }),
    }),

    // In-house employee forms.
    listMyForms: builder.query({
      query: () => "/forms/my_forms",
      providesTags: ['MyForms'],
    }),
    submitForm: builder.mutation({
      query: (p) => ({ url: `/forms/${p.form_key}/submit`, method: "POST", body: { responses: p.responses } }),
      invalidatesTags: ['MyForms'],
    }),
    // Who filed, when, who they report to, the manager gates — NO answers.
    // TECH or CONFIDENTIAL_REVIEWER.
    listFormSubmissions: builder.query({
      query: (p) => `/forms/${p.form_key}/submissions`,
    }),
    // One person's Role Alignment answers + their manager's assessment.
    // CONFIDENTIAL_REVIEWER only, and every fetch is logged server-side, so
    // nothing is kept once the row is collapsed: re-opening it is a new read.
    getRoleAlignmentResponses: builder.query({
      query: (p) => `/forms/role_alignment/submissions/${p.submission_id}/responses`,
      keepUnusedDataFor: 0,
    }),
    // The answers for a set of submissions, fetched only when an Ask is sent.
    // CONFIDENTIAL_REVIEWER only; logged as one batch read.
    fetchRoleAlignmentResponses: builder.mutation({
      query: (body) => ({ url: "/forms/role_alignment/responses", method: "POST", body }),
    }),
    // Completion stats for a form — counts, names and dates only, no answers.
    getFormDashboard: builder.query({
      query: (p) => `/forms/${p.form_key}/dashboard`,
    }),
    // Per-submission manager gates: whether the person's direct manager is asked
    // for an assessment, and whether they may read the questionnaire itself.
    setFormManagerGates: builder.mutation({
      query: (p) => ({
        url: `/forms/${p.form_key}/manager_gates`,
        method: "POST",
        body: { submission_id: p.submission_id, requested: p.requested, can_view: p.can_view },
      }),
    }),
    // Email one manager that an assessment is waiting for them. Separate from
    // the gate above on purpose: HR can tick the request now and chase later,
    // or chase again, without changing what anyone is allowed to see.
    notifyFormManager: builder.mutation({
      query: (p) => ({
        url: `/forms/${p.form_key}/notify_manager`,
        method: "POST",
        body: { submission_id: p.submission_id },
      }),
    }),
    listFormCatalog: builder.query({
      query: () => "/forms/catalog",
    }),
    // HR-configured recipients emailed when a form is submitted.
    listFormNotifyRecipients: builder.query({
      query: (arg) => ({
        url: "/forms/notify_recipients",
        params: (arg && arg.form_key) ? { form_key: arg.form_key } : undefined,
      }),
      providesTags: ['FormNotifyRecipients'],
    }),
    addFormNotifyRecipient: builder.mutation({
      query: (body) => ({ url: "/forms/notify_recipients", method: "POST", body }),
      invalidatesTags: ['FormNotifyRecipients'],
    }),
    deleteFormNotifyRecipient: builder.mutation({
      query: (p) => ({ url: "/forms/notify_recipients", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['FormNotifyRecipients'],
    }),

    // Internal applications against a posting.
    submitJobApplication: builder.mutation({
      query: (p) => ({ url: "/job_openings/apply", method: "POST", params: { id: p.id }, body: p.body }),
    }),
    getMyJobApplication: builder.query({
      query: (p) => ({ url: "/job_openings/my_application", params: { id: p.id } }),
    }),
    listJobApplications: builder.query({
      // HR-only. Omit id to list every application across postings.
      query: (arg) => ({ url: "/job_applications", params: (arg && arg.id) ? { id: arg.id } : undefined }),
    }),
    // Postings the caller is the hiring manager for, each with its applicants.
    // Not HR-gated — a manager sees only their own active, unfilled postings.
    listManagedJobOpenings: builder.query({
      query: () => ({ url: "/job_openings/managed" }),
    }),

    // ── AI Activity Tracking ──────────────────────────────────────────────────

    listAiActivity: builder.query({
      query: () => "/ai_activity",
      providesTags: ['AiActivity'],
    }),

    confirmAiActivity: builder.mutation({
      query: (body) => ({ url: "/ai_activity/confirm", method: "POST", body }),
      invalidatesTags: ['AiActivity'],
    }),

    // ── Admin impersonation ───────────────────────────────────────────────────

    impersonate: builder.mutation({
      query: (body) => ({ url: "/impersonate", method: "POST", body }),
    }),

    impersonateReturn: builder.mutation({
      query: () => ({ url: "/impersonate/return", method: "POST" }),
    }),

    // ── Tech Projects ─────────────────────────────────────────────────────────

    listTechProjects: builder.query({
      query: () => "/tech_projects",
      providesTags: ['TechProjects'],
    }),

    createTechProject: builder.mutation({
      query: (body) => ({ url: "/tech_projects", method: "POST", body }),
      invalidatesTags: ['TechProjects'],
    }),

    updateTechProjectStatus: builder.mutation({
      query: (body) => ({ url: "/tech_projects/status", method: "PUT", body }),
      invalidatesTags: ['TechProjects'],
    }),

    deleteTechProject: builder.mutation({
      query: (p) => ({ url: "/tech_projects", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['TechProjects'],
    }),

    // ── Tech Project Lanes ────────────────────────────────────────────────────

    listTechLanes: builder.query({
      query: (p) => ({ url: "/tech_lanes", params: p?.include_inactive ? { include_inactive: true } : {} }),
      providesTags: ['TechLanes'],
    }),

    createTechLane: builder.mutation({
      query: (body) => ({ url: "/tech_lanes", method: "POST", body }),
      invalidatesTags: ['TechLanes'],
    }),

    setTechLaneActive: builder.mutation({
      query: (body) => ({ url: "/tech_lanes/active", method: "PUT", body }),
      invalidatesTags: ['TechLanes', 'TechProjects'],
    }),

    deleteTechLane: builder.mutation({
      query: (p) => ({ url: "/tech_lanes", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['TechLanes', 'TechProjects'],
    }),

    // ── Company Directory ──────────────────────────────────────────────────────
    loadCompanyDirectory: builder.query({
      query: () => "/company_directory",
      providesTags: ['CompanyDirectory'],
    }),

    loadCompanyDirectoryFull: builder.query({
      // HR management view. Pass { includeTerminated: true } to include
      // terminated employees (the page's "show terminated" filter).
      query: (arg) => ({
        url: "/company_directory/all",
        params: (arg && arg.includeTerminated) ? { include_terminated: true } : undefined,
      }),
      providesTags: ['CompanyDirectory'],
    }),

    // Lightweight list of advisor names for the client detail advisor picker.
    // Shares the CompanyDirectory tag so toggling is_advisor in HR refreshes it.
    loadAdvisors: builder.query({
      query: () => "/company_directory/advisors",
      providesTags: ['CompanyDirectory'],
    }),

    loadMyReports: builder.query({
      query: () => "/company_directory/my_reports",
      providesTags: ['CompanyDirectory'],
    }),

    loadMyReportsUsage: builder.query({
      // Pass { month: 'YYYY-MM' } for a specific month, or nothing (or {}) for
      // the trailing 30-day window (server default). Usage is org-billing data,
      // not directory data, so it doesn't share the CompanyDirectory tag.
      query: (p) => (p && p.month)
        ? `/company_directory/my_reports/usage?month=${encodeURIComponent(p.month)}`
        : `/company_directory/my_reports/usage`,
    }),

    // Employee "request more Claude usage" — emails the configured hub
    // recipients. { requested_usd?: number, note?: string }.
    requestClaudeUsage: builder.mutation({
      query: (p = {}) => ({
        url: "/company_directory/request_usage",
        method: "POST",
        body: { requested_usd: p.requested_usd ?? null, note: p.note ?? null },
      }),
    }),

    loadAllClaudeSpend: builder.query({
      // Org-wide per-employee Claude spend for the executive AI page. EXEC-gated
      // on the server. Pass { month: 'YYYY-MM' } for a specific month, or nothing
      // (or {}) for the trailing 30-day window (the server default) — the latter
      // keeps figures meaningful right after a month rollover.
      query: (p) => (p && p.month)
        ? `/company_directory/claude_spend?month=${encodeURIComponent(p.month)}`
        : `/company_directory/claude_spend`,
    }),

    // Pre-flight for the Calendar nav item: is the feature on, and has this
    // person been granted it on their directory entry. Needs no Graph token,
    // unlike every other calendar endpoint.
    getCalendarAccess: builder.query({
      query: () => ({ url: "/calendar/access" }),
    }),

    // Events page (the firm's shared events calendar). Status is the token-free
    // pre-flight for the HR menu row; the events read carries the delegated
    // token in the body, so the cache key is the date range (one per month
    // grid) and never the token.
    // Tags are inert here (no tagTypes), so the HR settings section refetches
    // the status itself after a save.
    getEventsCalendarStatus: builder.query({
      query: () => ({ url: "/events-calendar/status" }),
    }),
    getEventsCalendar: builder.query({
      query: (p) => ({
        url: "/events-calendar/events",
        method: "POST",
        body: { graph_token: p.graphToken, start: p.start, end: p.end },
      }),
      serializeQueryArgs: ({ queryArgs }) => `eventsCalendar:${queryArgs.start}:${queryArgs.end}`,
      keepUnusedDataFor: 300,
    }),
    getEventsCalendarSettings: builder.query({
      query: () => ({ url: "/events-calendar/settings" }),
    }),
    setEventsCalendarSettings: builder.mutation({
      query: (body) => ({ url: "/events-calendar/settings", method: "PUT", body }),
    }),

    upsertCompanyDirectoryEntry: builder.mutation({
      query: (body) => ({ url: "/company_directory", method: "PUT", body }),
      invalidatesTags: ['CompanyDirectory'],
    }),

    deleteCompanyDirectoryEntry: builder.mutation({
      query: (p) => ({ url: "/company_directory", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['CompanyDirectory'],
    }),

    setDirectoryActive: builder.mutation({
      query: (body) => ({ url: "/company_directory/set_active", method: "POST", body }),
      invalidatesTags: ['CompanyDirectory'],
    }),

    listFirms: builder.query({
      query: () => "/firms",
      providesTags: ['Firms'],
    }),

    addFirm: builder.mutation({
      query: (body) => ({ url: "/firms", method: "POST", body }),
      invalidatesTags: ['Firms'],
    }),

    deleteFirm: builder.mutation({
      query: (p) => ({ url: "/firms", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['Firms'],
    }),

    uploadCompanyDirectoryPhoto: builder.mutation({
      query: ({ directory_id, file }) => {
        const form = new FormData();
        form.append('directory_id', directory_id);
        form.append('file', file);
        return { url: "/company_directory/photo", method: "POST", body: form };
      },
      invalidatesTags: ['CompanyDirectory'],
    }),

    // ── Email Domain Migration Tracking ──────────────────────────────────────
    loadEmailMigration: builder.query({
      query: () => "/email_migration_load",
      providesTags: ['EmailMigration'],
    }),

    upsertEmailMigration: builder.mutation({
      query: (body) => ({ url: "/email_migration_upsert", method: "POST", body }),
      invalidatesTags: ['EmailMigration'],
    }),

    refreshEmailMigrationPrimary: builder.mutation({
      query: () => ({ url: "/email_migration_refresh_primary", method: "POST" }),
      // Auto-complete may have flipped some rows, so refresh the list cache.
      invalidatesTags: ['EmailMigration'],
    }),

    // ── Email Help (unauthenticated, used on the login page) ──────────────────
    lookupEmailHelpByLastName: builder.mutation({
      query: (p) => ({
        url: "/email_help/lookup_by_lastname",
        method: "POST",
        body: { last_name: p.last_name },
      }),
    }),

    requestEmailHelpResetCode: builder.mutation({
      query: (p) => ({
        url: "/email_help/request_reset_code",
        method: "POST",
        body: { directory_id: p.directory_id },
      }),
    }),

    verifyAndResetEmailHelpPassword: builder.mutation({
      query: (p) => ({
        url: "/email_help/verify_and_reset",
        method: "POST",
        body: { directory_id: p.directory_id, code: p.code },
      }),
    }),

    // ── HR Announcements ──────────────────────────────────────────────────────
    listActiveAnnouncements: builder.query({
      query: () => "/announcements/active",
      providesTags: ['Announcements'],
    }),

    listAllAnnouncements: builder.query({
      query: () => "/announcements/all",
      providesTags: ['Announcements'],
    }),

    createAnnouncement: builder.mutation({
      query: (body) => ({ url: "/announcements", method: "POST", body }),
      invalidatesTags: ['Announcements'],
    }),

    updateAnnouncement: builder.mutation({
      query: (body) => ({ url: "/announcements", method: "PUT", body }),
      invalidatesTags: ['Announcements'],
    }),

    deleteAnnouncement: builder.mutation({
      query: (p) => ({ url: "/announcements", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['Announcements'],
    }),

    // ── Company Holidays & Summer Hours ───────────────────────────────────────
    listHolidays: builder.query({
      query: (p = {}) => ({ url: "/holidays", params: p.year ? { year: p.year } : {} }),
      providesTags: ['Holidays'],
    }),

    createHoliday: builder.mutation({
      query: (body) => ({ url: "/holidays", method: "POST", body }),
      invalidatesTags: ['Holidays'],
    }),

    updateHoliday: builder.mutation({
      query: (body) => ({ url: "/holidays", method: "PUT", body }),
      invalidatesTags: ['Holidays'],
    }),

    deleteHoliday: builder.mutation({
      query: (p) => ({ url: "/holidays", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['Holidays'],
    }),

    listSummerSchedules: builder.query({
      query: (p = {}) => ({ url: "/summer_schedules", params: p.year ? { year: p.year } : {} }),
      providesTags: ['SummerSchedules'],
    }),

    createSummerSchedule: builder.mutation({
      query: (body) => ({ url: "/summer_schedules", method: "POST", body }),
      invalidatesTags: ['SummerSchedules'],
    }),

    updateSummerSchedule: builder.mutation({
      query: (body) => ({ url: "/summer_schedules", method: "PUT", body }),
      invalidatesTags: ['SummerSchedules'],
    }),

    deleteSummerSchedule: builder.mutation({
      query: (p) => ({ url: "/summer_schedules", method: "DELETE", params: { id: p.id } }),
      invalidatesTags: ['SummerSchedules'],
    }),

    // ── Training / LMS (Compliance authoring) ─────────────────────────────────

    listTrainingCourses: builder.query({
      query: () => "/training/courses",
      providesTags: ['TrainingCourses'],
    }),

    getTrainingCourse: builder.query({
      query: (p) => `/training/courses/${p.id}`,
      providesTags: (result, error, arg) => [{ type: 'TrainingCourse', id: arg.id }],
    }),

    createTrainingCourse: builder.mutation({
      query: (body) => ({ url: "/training/courses", method: "POST", body }),
      invalidatesTags: ['TrainingCourses'],
    }),

    updateTrainingCourse: builder.mutation({
      query: (p) => ({ url: `/training/courses/${p.id}`, method: "PATCH", body: p.body }),
      // Editing a pending-review course auto-resets it to draft on the server,
      // so the admin's pending queue may change too.
      invalidatesTags: (result, error, arg) => [
        'TrainingCourses',
        'TrainingPendingReviews',
        { type: 'TrainingCourse', id: arg.id },
      ],
    }),

    setTrainingCourseStatus: builder.mutation({
      query: (p) => ({
        url: `/training/courses/${p.id}/status`,
        method: "PUT",
        body: { status: p.status },
      }),
      invalidatesTags: (result, error, arg) => [
        'TrainingCourses',
        { type: 'TrainingCourse', id: arg.id },
        'MyTrainingCourses',
        { type: 'MyTrainingCourse', id: arg.id },
      ],
    }),

    deleteTrainingCourse: builder.mutation({
      query: (p) => ({ url: `/training/courses/${p.id}`, method: "DELETE" }),
      invalidatesTags: (result, error, arg) => [
        'TrainingCourses',
        'MyTrainingCourses',
        { type: 'MyTrainingCourse', id: arg.id },
      ],
    }),

    addTrainingQuizModule: builder.mutation({
      query: (p) => ({
        url: `/training/courses/${p.courseId}/modules/quiz`,
        method: "POST",
        body: p.body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TrainingCourse', id: arg.courseId },
        'TrainingPendingReviews',
      ],
    }),

    addTrainingVideoModule: builder.mutation({
      query: (p) => ({
        url: `/training/courses/${p.courseId}/modules/video`,
        method: "POST",
        body: p.body,
        formData: true,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TrainingCourse', id: arg.courseId },
        'TrainingPendingReviews',
      ],
    }),

    updateTrainingModule: builder.mutation({
      query: (p) => ({ url: `/training/modules/${p.id}`, method: "PATCH", body: p.body }),
      invalidatesTags: (result, error, arg) => {
        const tags = ['TrainingPendingReviews'];
        if (arg.courseId) tags.push({ type: 'TrainingCourse', id: arg.courseId });
        return tags;
      },
    }),

    deleteTrainingModule: builder.mutation({
      query: (p) => ({ url: `/training/modules/${p.id}`, method: "DELETE" }),
      invalidatesTags: (result, error, arg) => {
        const tags = ['TrainingPendingReviews'];
        if (arg.courseId) tags.push({ type: 'TrainingCourse', id: arg.courseId });
        return tags;
      },
    }),

    reorderTrainingModules: builder.mutation({
      query: (p) => ({
        url: `/training/courses/${p.courseId}/modules/reorder`,
        method: "PUT",
        body: { ordered_ids: p.ordered_ids },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TrainingCourse', id: arg.courseId },
        'TrainingPendingReviews',
      ],
    }),

    getTrainingVideoUrl: builder.mutation({
      query: (p) => ({ url: `/training/modules/${p.id}/video_url`, method: "GET" }),
    }),

    getTrainingCourseResults: builder.query({
      query: (p) => `/training/courses/${p.id}/results`,
      providesTags: (result, error, arg) => [{ type: 'TrainingCourseResults', id: arg.id }],
    }),

    listTrainingSandboxUsers: builder.query({
      query: (p) => `/training/courses/${p.id}/sandbox_users`,
      providesTags: (result, error, arg) => [{ type: 'TrainingSandboxUsers', id: arg.id }],
    }),

    submitTrainingReview: builder.mutation({
      query: (p) => ({ url: `/training/courses/${p.id}/submit_review`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [
        'TrainingCourses',
        'TrainingPendingReviews',
        { type: 'TrainingCourse', id: arg.id },
      ],
    }),

    withdrawTrainingReview: builder.mutation({
      query: (p) => ({ url: `/training/courses/${p.id}/withdraw_review`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [
        'TrainingCourses',
        'TrainingPendingReviews',
        { type: 'TrainingCourse', id: arg.id },
      ],
    }),

    approveTrainingReview: builder.mutation({
      query: (p) => ({ url: `/training/courses/${p.id}/review/approve`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [
        'TrainingCourses',
        'TrainingPendingReviews',
        'MyTrainingCourses',
        { type: 'TrainingCourse', id: arg.id },
      ],
    }),

    rejectTrainingReview: builder.mutation({
      query: (p) => ({
        url: `/training/courses/${p.id}/review/reject`,
        method: "POST",
        body: { reason: p.reason || null },
      }),
      invalidatesTags: (result, error, arg) => [
        'TrainingCourses',
        'TrainingPendingReviews',
        { type: 'TrainingCourse', id: arg.id },
      ],
    }),

    listTrainingPendingReviews: builder.query({
      query: () => `/training/pending_reviews`,
      providesTags: ['TrainingPendingReviews'],
    }),

    countTrainingPendingReviews: builder.query({
      query: () => `/training/pending_reviews/count`,
      providesTags: ['TrainingPendingReviews'],
    }),

    addTrainingSandboxUser: builder.mutation({
      query: (p) => ({
        url: `/training/courses/${p.id}/sandbox_users`,
        method: "POST",
        body: p.email ? { email: p.email } : { user_uuid: p.user_uuid },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TrainingSandboxUsers', id: arg.id },
        'MyTrainingCourses',
      ],
    }),

    removeTrainingSandboxUser: builder.mutation({
      query: (p) => ({
        url: `/training/courses/${p.id}/sandbox_users/${encodeURIComponent(p.user_uuid)}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TrainingSandboxUsers', id: arg.id },
        'MyTrainingCourses',
      ],
    }),

    // ── Training / LMS (learner side) ─────────────────────────────────────────

    listMyTrainingCourses: builder.query({
      query: () => "/training/my_courses",
      providesTags: ['MyTrainingCourses'],
    }),

    getMyTrainingCourse: builder.query({
      query: (p) => `/training/my_courses/${p.id}`,
      providesTags: (result, error, arg) => [{ type: 'MyTrainingCourse', id: arg.id }],
    }),

    recordVideoProgress: builder.mutation({
      query: (p) => ({
        url: `/training/my_courses/${p.courseId}/modules/${p.moduleId}/progress`,
        method: "POST",
        body: {
          max_watched_sec: p.max_watched_sec,
          duration_sec: p.duration_sec,
        },
      }),
      // Don't invalidate every heartbeat; the learner page refetches the course
      // explicitly when it cares (e.g. when the video reaches completion).
    }),

    submitQuizAttempt: builder.mutation({
      query: (p) => ({
        url: `/training/my_courses/${p.courseId}/modules/${p.moduleId}/quiz`,
        method: "POST",
        body: { answers: p.answers },
      }),
      // Deliberately do NOT invalidate `MyTrainingCourse` here. A failed quiz
      // wipes server-side video progress, which would recompute the active
      // module index and bounce the learner off the result page before they
      // finish reading it. The learner page refetches explicitly when they
      // click Continue (pass) or Take me back to the start (fail).
      // We still invalidate the list so the My Courses summary picks up the
      // new pass/fail status next time it's rendered.
      invalidatesTags: ['MyTrainingCourses'],
    }),

    getMyTrainingVideoUrl: builder.mutation({
      query: (p) => ({ url: `/training/my_courses/modules/${p.id}/video_url`, method: "GET" }),
    }),

    // ── Wealth-management clients (Work side) ────────────────────────────────

    hubspotSearchClients: builder.query({
      query: (p) => `/clients/hubspot_search?q=${encodeURIComponent(p.q)}${p.mock ? '&mock=true' : ''}`,
      // No providesTags — search results are ephemeral, no need to refetch
      // when other endpoints invalidate.
    }),

    listClientUsers: builder.query({
      query: () => "/clients",
      providesTags: ['ClientUsers'],
    }),

    createClientUser: builder.mutation({
      query: (p) => ({
        url: "/clients",
        method: "POST",
        body: {
          email: p.email,
          first_name: p.first_name,
          last_name: p.last_name,
          hubspot_contact_id: p.hubspot_contact_id,
        },
      }),
      invalidatesTags: ['ClientUsers'],
    }),

    // Add one member to an existing household (wizard Back → edit). `member` is
    // {first_name,last_name,email,phone,association,hubspot_contact_id}.
    addHouseholdMember: builder.mutation({
      query: (p) => ({ url: `/clients/${p.primary_public_id}/household_members`, method: "POST", body: p.member }),
      invalidatesTags: ['ClientUsers'],
    }),

    // Create a whole household (name + members with associations) in one call —
    // the new-client wizard's Step 1. Returns { household_id, members, primary }.
    createClientHousehold: builder.mutation({
      query: (p) => ({
        url: "/clients/household",
        method: "POST",
        body: {
          household_name: p.household_name,
          members: p.members,
          advisor_name: p.advisor_name,
          advisor_name_2: p.advisor_name_2,
          gemstone: p.gemstone,
          workflow_status: p.workflow_status,
          // Optional tie to an existing FSC household — see the fsc_link
          // endpoints below.
          fsc_household_id: p.fsc_household_id,
          // The FSC office the household is filed at — see listMyOffices. The
          // server overrides it when fsc_household_id names an office.
          office_fsc_id: p.office_fsc_id,
          point_people: p.point_people,
          is_test: p.is_test,
        },
      }),
      invalidatesTags: ['ClientUsers'],
    }),

    getClientUser: builder.query({
      query: (p) => `/clients/${p.id}`,
      providesTags: (result, error, arg) => [{ type: 'ClientUser', id: arg.id }],
    }),

    // ── Portal household ↔ FSC household (the "AUM client" link) ──────────
    // One link per hub household, stored in crm_identity_crosswalk with
    // source_system='portal'. The GET also returns the household's money
    // facts (AUM, client-since, net flow) read out of the nightly Orion
    // cache — no Orion call happens on this request. Tags are inert in this
    // slice, so the detail page refetch()es after a link/unlink.
    getClientFscLink: builder.query({
      query: (p) => `/clients/${p.id}/fsc_link`,
    }),
    setClientFscLink: builder.mutation({
      query: (p) => ({
        url: `/clients/${p.id}/fsc_link`,
        method: 'PUT',
        body: { fsc_household_id: p.fsc_household_id },
      }),
    }),
    clearClientFscLink: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/fsc_link`, method: 'DELETE' }),
    }),

    // "Log in as" a client for support — WORK + ADMIN. Mints a portal session
    // token for the target; the employee's own session is untouched, so
    // returning is just discarding the portal token. Mirrors
    // impersonateEntrepreneurUser above and the employee `impersonate`.
    impersonateClientUser: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/impersonate`, method: 'POST' }),
    }),

    // "Your documents are waiting" nudge. The GET is a preview - what the
    // email would list and whether it can be sent at all - so the advisor
    // sees the exact list before a real client hears from us.
    getClientDocumentsReminder: builder.query({
      query: (p) => `/clients/${p.id}/documents_reminder`,
    }),
    sendClientDocumentsReminder: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/documents_reminder`, method: 'POST' }),
    }),

    addClientPointPerson: builder.mutation({
      // p.kind: 'employee' (pass directory_id) or 'email' (pass email).
      query: (p) => ({
        url: `/clients/${p.id}/point_people`,
        method: "POST",
        body: { kind: p.kind, directory_id: p.directory_id, email: p.email },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'ClientUser', id: arg.id }],
    }),

    removeClientPointPerson: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/point_people/${p.point_person_id}`, method: "DELETE" }),
      invalidatesTags: (result, error, arg) => [{ type: 'ClientUser', id: arg.id }],
    }),

    setClientDocuments: builder.mutation({
      query: (p) => {
        const body = { type_keys: p.type_keys };
        // Multiple custom requests, reconciled by id. Three-state: omit to leave
        // them unchanged, [] to clear all, or a list of {id?, label} to set.
        if (p.custom_requests !== undefined) body.custom_requests = p.custom_requests;
        // Legacy single custom request (still supported by the backend).
        if (p.custom_request !== undefined) body.custom_request = p.custom_request;
        return { url: `/clients/${p.id}/documents`, method: "PUT", body };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'ClientUser', id: arg.id }],
    }),

    publishClientUser: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/publish`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientUser', id: arg.id },
        'ClientUsers',
      ],
    }),

    unpublishClientUser: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/unpublish`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientUser', id: arg.id },
        'ClientUsers',
      ],
    }),

    linkClientHousehold: builder.mutation({
      // `p.id` and `p.partner_id` here are *public_id* strings — the
      // sequential int is never sent over the wire.
      query: (p) => ({
        url: `/clients/${p.id}/household`,
        method: "POST",
        body: { partner_public_id: p.partner_id },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientUser', id: arg.id },
        { type: 'ClientUser', id: arg.partner_id },
        'ClientUsers',
      ],
    }),

    createClientLogin: builder.mutation({
      // Pass { send_email: true } to also email the client their temp password
      // + portal link (from advisors@). The temp password is still returned.
      query: (p) => ({
        url: `/clients/${p.id}/login`,
        method: "POST",
        params: p.send_email ? { send_email: true } : undefined,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientUser', id: arg.id },
        'ClientUsers',
      ],
    }),

    updateClientUser: builder.mutation({
      query: (p) => {
        const body = {};
        // Only include fields the caller actually sent so the backend can
        // distinguish "leave unchanged" from "clear" (empty string).
        if (p.first_name !== undefined) body.first_name = p.first_name;
        if (p.last_name !== undefined) body.last_name = p.last_name;
        if (p.email !== undefined) body.email = p.email;
        if (p.advisor_name !== undefined) body.advisor_name = p.advisor_name;
        if (p.advisor_name_2 !== undefined) body.advisor_name_2 = p.advisor_name_2;
        if (p.gemstone !== undefined) body.gemstone = p.gemstone;
        if (p.workflow_status !== undefined) body.workflow_status = p.workflow_status;
        // firm_id: send null/0 to clear (unassign), an id to set, omit to leave.
        if (p.firm_id !== undefined) body.firm_id = p.firm_id;
        if (p.phone !== undefined) body.phone = p.phone;
        if (p.association !== undefined) body.association = p.association;
        // onetime_fee_cents: null clears, int sets, omit leaves.
        if (p.onetime_fee_cents !== undefined) body.onetime_fee_cents = p.onetime_fee_cents;
        // office_fsc_id: reassigns the client AND their whole household. No
        // clear — the backend rejects an empty value, because a client with no
        // office is visible to full-access users only.
        if (p.office_fsc_id !== undefined) body.office_fsc_id = p.office_fsc_id;
        return { url: `/clients/${p.id}`, method: "PATCH", body };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientUser', id: arg.id },
        'ClientUsers',
      ],
    }),

    deleteClientUser: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}`, method: "DELETE" }),
      invalidatesTags: ['ClientUsers'],
    }),

    deactivateClientUser: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/deactivate`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientUser', id: arg.id },
        'ClientUsers',
      ],
    }),

    reactivateClientUser: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/reactivate`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [
        { type: 'ClientUser', id: arg.id },
        'ClientUsers',
      ],
    }),

    getClientUploadDownloadUrl: builder.mutation({
      // Mutation rather than query so each "Download"/"View" click forces a
      // fresh presigned URL (the previous one may have expired). Pass
      // { disposition: 'inline' } to open the file in a tab instead of downloading.
      query: (p) => ({
        url: `/clients/uploads/${p.upload_id}/download_url`,
        method: "GET",
        params: p.disposition ? { disposition: p.disposition } : undefined,
      }),
    }),

    reExtractClientUpload: builder.mutation({
      // Re-runs Textract against an already-stored S3 object. We accept
      // an optional `client_id` purely to drive cache invalidation so
      // the ClientDetail view refetches after the dispatch flips the
      // upload's status.
      query: (p) => ({ url: `/clients/uploads/${p.upload_id}/re_extract`, method: "POST" }),
      invalidatesTags: (result, error, arg) =>
        arg?.client_id ? [{ type: 'ClientUser', id: arg.client_id }] : [],
    }),

    setClientUploadOwner: builder.mutation({
      // Advisor override of the AI household-owner guess for a file.
      // `kind` is 'member' (with client_public_id), 'joint', 'unknown', or
      // 'auto' to clear the override. `client_id` (the client public_id) is
      // accepted only to drive ClientDetail cache invalidation.
      query: (p) => ({
        url: `/clients/uploads/${p.upload_id}/owner`,
        method: "PATCH",
        body: { kind: p.kind, client_public_id: p.client_public_id },
      }),
      invalidatesTags: (result, error, arg) =>
        arg?.client_id ? [{ type: 'ClientUser', id: arg.client_id }] : [],
    }),

    setClientUploadYear: builder.mutation({
      // Advisor tag: which year a document is ABOUT, not upload date.
      // Pass document_year: null to clear it. No tag invalidation — the
      // Work-side document browser has no cache tags today, callers refetch.
      query: (p) => ({
        url: `/clients/uploads/${p.upload_id}/year`,
        method: "PATCH",
        body: { document_year: p.document_year },
      }),
    }),

    getClientDocumentCatalog: builder.query({
      query: () => "/clients/document_catalog",
      // Catalog is a code constant — cache aggressively across components.
      providesTags: ['ClientDocCatalog'],
    }),

    // ── Documents we share OUT to a client ────────────────────────────────
    // The other direction from every `uploads` endpoint above: the advisor
    // sends, the household reads it in the portal. Tags are inert in this
    // slice, so the panel refetch()es after each mutation.

    // The OUTBOUND taxonomy (financial plan, review packet, …) — a different
    // list from getClientDocumentCatalog, which is what we ask clients FOR.
    getClientSharedCatalog: builder.query({
      query: () => "/clients/shared_catalog",
    }),

    // Everything sent to this client's household, with read receipts, plus
    // the household roster the audience picker needs.
    listClientSharedDocuments: builder.query({
      query: (p) => ({
        url: `/clients/${p.id}/shared_documents`,
        params: p.include_revoked ? { include_revoked: true } : undefined,
      }),
    }),

    // One file per call — the caller loops, so a failure on the third file
    // leaves the first two shared rather than rolling everything back.
    // Metadata rides as query params because the body is the file itself.
    shareClientDocument: builder.mutation({
      query: (p) => {
        const form = new FormData();
        form.append('file', p.file);
        const params = { type_key: p.type_key, audience: p.audience || 'household' };
        if (p.title) params.title = p.title;
        if (p.document_year) params.document_year = p.document_year;
        if (p.note) params.note = p.note;
        if (p.recipient_ids?.length) params.recipient_ids = p.recipient_ids.join(',');
        return { url: `/clients/${p.id}/shared_documents`, method: 'POST', body: form, params };
      },
    }),

    updateClientSharedDocument: builder.mutation({
      // Retag only — title, type, year. Pass document_year: 0 to clear it.
      query: (p) => ({
        url: `/clients/shared/${p.shared_id}`,
        method: 'PATCH',
        body: { title: p.title, type_key: p.type_key, document_year: p.document_year },
      }),
    }),

    // Un-share. The client loses access at once; the S3 object stays (WORM).
    revokeClientSharedDocument: builder.mutation({
      query: (p) => ({ url: `/clients/shared/${p.shared_id}/revoke`, method: 'POST' }),
    }),

    getClientSharedDownloadUrl: builder.mutation({
      query: (p) => ({
        url: `/clients/shared/${p.shared_id}/download_url`,
        method: 'GET',
        params: p.disposition ? { disposition: p.disposition } : undefined,
      }),
    }),

    // Videos only: a reusable ~2 hour playback link for a <video> element,
    // which makes many Range requests and can't use a one-time link.
    getClientSharedStreamUrl: builder.mutation({
      query: (p) => ({ url: `/clients/shared/${p.shared_id}/stream_url`, method: 'GET' }),
    }),

    // Shared videos go up in chunks (nginx caps a request at 26 MB):
    // start → part × N → complete, or abort. `p.share` is the same labelling
    // payload for start and complete — start checks it before any bytes move.
    startSharedVideo: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/shared_videos/start`, method: 'POST', body: p.share }),
    }),
    // The body is a raw Blob slice, which fetchBaseQuery passes through as-is.
    uploadSharedVideoPart: builder.mutation({
      query: (p) => ({
        url: `/clients/${p.id}/shared_videos/part`,
        method: 'PUT',
        params: { s3_key: p.s3_key, upload_id: p.upload_id, part_number: p.part_number },
        body: p.chunk,
      }),
    }),
    completeSharedVideo: builder.mutation({
      query: (p) => ({
        url: `/clients/${p.id}/shared_videos/complete`,
        method: 'POST',
        body: { ...p.share, s3_key: p.s3_key, upload_id: p.upload_id },
      }),
    }),
    abortSharedVideo: builder.mutation({
      query: (p) => ({
        url: `/clients/${p.id}/shared_videos/abort`,
        method: 'POST',
        body: { s3_key: p.s3_key, upload_id: p.upload_id },
      }),
    }),

    // "New documents are waiting" — the GET previews whether it can be sent
    // and who a reply reaches; the POST sends ONE email covering every id.
    getClientSharedNotifyPreview: builder.query({
      query: (p) => `/clients/${p.id}/shared_documents/notify`,
    }),
    sendClientSharedNotification: builder.mutation({
      query: (p) => ({
        url: `/clients/${p.id}/shared_documents/notify`,
        method: 'POST',
        body: { ids: p.ids },
      }),
    }),

    // Firms an employee can assign a client to (id + name), for the firm picker.
    listClientFirms: builder.query({
      query: () => "/clients/firms",
    }),

    // The FSC offices the signed-in user may file a client into, for the
    // new-client Office picker and the reassignment control. Returns
    // { offices: [{fsc_id, name, kind}], prefill } — `prefill` is the only
    // office when there is exactly one, and null otherwise.
    listMyOffices: builder.query({
      query: () => "/clients/my_offices",
    }),

    // DocuSign FP Consulting Agreement (Work side).
    getClientAgreement: builder.query({
      query: (p) => ({ url: `/clients/${p.id}/agreement` }),
      providesTags: (result, error, arg) => [{ type: 'ClientAgreement', id: arg.id }],
    }),
    sendClientAgreement: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/agreement/send`, method: "POST" }),
      invalidatesTags: (result, error, arg) => [{ type: 'ClientAgreement', id: arg.id }],
    }),
    getAdvisorSigningUrl: builder.mutation({
      query: (p) => ({ url: `/clients/${p.id}/agreement/advisor_signing_url`, method: "POST", body: { return_url: p.return_url } }),
    }),

    // Cross-client document search for the Work Documents page. `params` is the
    // filter object (q, type_key, category, status, institution, advisor,
    // workflow_status, firm_id, letter, date_from, date_to, sort) — undefined
    // keys are dropped so the URL stays clean.
    searchClientDocuments: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: "/clients/documents", params: clean };
      },
    }),

    // ── Site feedback ─────────────────────────────────────────────────────────

    createSiteFeedback: builder.mutation({
      query: (p) => ({
        url: "/site_feedback",
        method: "POST",
        body: { category: p.category, message: p.message, page_url: p.page_url },
      }),
      invalidatesTags: ['SiteFeedback'],
    }),

    listSiteFeedback: builder.query({
      query: () => "/site_feedback",
      providesTags: ['SiteFeedback'],
    }),

    // ── Reg S-P software register ──────────────────────────────────────────
    listRegSpSoftware: builder.query({
      query: () => "/reg_sp/software",
    }),
    getRegSpSoftware: builder.query({
      query: (p) => `/reg_sp/software/${p.id}`,
    }),
    createRegSpSoftware: builder.mutation({
      query: (p) => ({ url: "/reg_sp/software", method: "POST", body: p.body }),
    }),
    updateRegSpSoftware: builder.mutation({
      query: (p) => ({ url: `/reg_sp/software/${p.id}`, method: "PUT", body: p.body }),
    }),
    deleteRegSpSoftware: builder.mutation({
      query: (p) => ({ url: `/reg_sp/software/${p.id}`, method: "DELETE" }),
    }),
    signoffRegSpSoftware: builder.mutation({
      query: (p) => ({ url: `/reg_sp/software/${p.id}/signoff`, method: "POST", body: { section: p.section, approved: p.approved } }),
    }),
    getRegSpAudit: builder.query({
      query: (p) => `/reg_sp/software/${p.id}/audit`,
    }),
    addRegSpLink: builder.mutation({
      query: (p) => ({ url: `/reg_sp/software/${p.id}/links`, method: "POST", body: { label: p.label, url: p.url } }),
    }),
    deleteRegSpLink: builder.mutation({
      query: (p) => ({ url: `/reg_sp/software/${p.id}/links/${p.linkId}`, method: "DELETE" }),
    }),
    uploadRegSpDocument: builder.mutation({
      query: (p) => ({ url: `/reg_sp/software/${p.id}/documents`, method: "POST", body: p.body, formData: true }),
    }),
    getRegSpDocumentUrl: builder.mutation({
      query: (p) => ({ url: `/reg_sp/documents/${p.docId}/url`, method: "GET" }),
    }),
    deleteRegSpDocument: builder.mutation({
      query: (p) => ({ url: `/reg_sp/documents/${p.docId}`, method: "DELETE" }),
    }),

    // ── FSC client book (Work side) ────────────────────────────────────────
    // Backed by the local fsc_households cache, not live Salesforce — see
    // api/app/routers/fsc_households.py. Document counts and the portal badge
    // are computed by join, so they cannot drift from the underlying tables.
    searchFscHouseholds: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '' && v !== false) clean[k] = v;
        });
        return { url: '/fsc/households', params: clean };
      },
    }),
    // letter_field is 'name' | 'last_name' — FSC labels households by FIRST
    // name, so the A-Z strip has to be able to work off the paired person's
    // surname instead.
    listFscFirms: builder.query({
      query: () => '/fsc/households/firms',
    }),
    listFscReps: builder.query({
      // Narrowed by office when one is selected, so the advisor list stays short.
      query: (p = {}) => ({ url: '/fsc/households/reps', params: p.firm ? { firm: p.firm } : {} }),
    }),
    getFscHousehold: builder.query({
      query: (p) => `/fsc/households/${p.id}`,
    }),
    listFscHouseholdDocuments: builder.query({
      query: (p) => ({ url: `/fsc/households/${p.id}/documents`, params: { limit: p.limit ?? 200 } }),
    }),
    getFscSyncStatus: builder.query({
      query: () => '/fsc/households/sync-status',
    }),

    // ── CRM (Work side) ────────────────────────────────────────────────────
    // The consolidated client book of the merging firms, built from each
    // firm's staged CRM into crm_clients - see api/app/routers/crm.py. The
    // summary takes the same filters as the list so the tiles describe what
    // is on screen.
    searchCrmClients: builder.query({
      query: (params = {}) => ({ url: '/crm/clients', params: cleanParams(params) }),
    }),
    getCrmSummary: builder.query({
      query: (params = {}) => ({ url: '/crm/summary', params: cleanParams(params) }),
    }),
    getCrmOptions: builder.query({
      query: () => '/crm/options',
    }),
    // "Ask": a typed question -> the list's filters. Returns filters only; the
    // page applies them and the list query does the rest.
    askCrm: builder.mutation({
      query: (body) => ({ url: '/crm/ask', method: 'POST', body }),
    }),
    getCrmClient: builder.query({
      query: (p) => `/crm/clients/${p.id}`,
    }),
    // CRM Calendar: a Redtail firm's office calendar, read live (the server
    // caches each window for a few minutes; `fresh` is the Refresh button).
    getCrmCalendarSources: builder.query({
      query: () => '/crm/calendar/sources',
    }),
    getCrmCalendar: builder.query({
      query: (params) => ({ url: '/crm/calendar', params: cleanParams(params) }),
    }),

    // ── Work -> Tasks ──────────────────────────────────────────────────────
    // Client service tasks and product queues - see api/app/routers/crm_tasks.py
    // (/crm-tasks, not /tasks: that is the To Do / Planner page). Tags are
    // inert in this app, so pages refetch after each mutation themselves.
    getCrmTaskLookups: builder.query({
      query: () => '/crm-tasks/lookups',
    }),
    getCrmTaskCounts: builder.query({
      query: () => '/crm-tasks/counts',
    }),
    listCrmTasks: builder.query({
      query: (params = {}) => ({ url: '/crm-tasks', params: cleanParams(params) }),
    }),
    getCrmTaskWorkload: builder.query({
      query: () => '/crm-tasks/workload',
    }),
    getCrmTask: builder.query({
      query: (id) => `/crm-tasks/${id}`,
    }),
    createCrmTask: builder.mutation({
      query: (body) => ({ url: '/crm-tasks', method: 'POST', body }),
    }),
    // Only the keys present in `changes` are changed (owner_uuid: null hands
    // the task back to its queue), so `changes` is sent as-is.
    updateCrmTask: builder.mutation({
      query: ({ id, changes }) => ({ url: `/crm-tasks/${id}`, method: 'PATCH', body: changes }),
    }),
    claimCrmTask: builder.mutation({
      query: (id) => ({ url: `/crm-tasks/${id}/claim`, method: 'POST' }),
    }),
    saveCrmTaskQueue: builder.mutation({
      query: (body) => ({ url: '/crm-tasks/admin/queues', method: 'POST', body }),
    }),
    saveCrmTaskType: builder.mutation({
      query: (body) => ({ url: '/crm-tasks/admin/types', method: 'POST', body }),
    }),

    // ── Agentic WM (Work side) ─────────────────────────────────────────────
    // Per-client conversations with the Prosperity Agentic Wealth Advisor -
    // see api/app/routers/agentic_wm.py. Asking is not here: the reply
    // streams as NDJSON, which RTK Query cannot read, so the page posts it
    // with fetch() (routes/work/AgenticWm.js).
    listAgenticWmThreads: builder.query({
      query: (params = {}) => ({ url: '/agentic-wm/threads', params: cleanParams(params) }),
    }),
    getAgenticWmThread: builder.query({
      query: (id) => `/agentic-wm/threads/${id}`,
    }),
    openAgenticWmThread: builder.mutation({
      query: (body) => ({ url: '/agentic-wm/threads', method: 'POST', body }),
    }),
    renameAgenticWmThread: builder.mutation({
      query: ({ id, title }) => ({ url: `/agentic-wm/threads/${id}`, method: 'PATCH', body: { title } }),
    }),
    archiveAgenticWmThread: builder.mutation({
      query: (id) => ({ url: `/agentic-wm/threads/${id}`, method: 'DELETE' }),
    }),
    // A tester's thumbs up (1) / down (-1) on one reply; 0 clears it.
    setAgenticWmFeedback: builder.mutation({
      query: ({ id, rating, comment }) => ({ url: `/agentic-wm/messages/${id}/feedback`, method: 'PUT',
                                             body: { rating, comment: comment || '' } }),
    }),
    // Super Admin -> Agentic WM Activity (api/app/routers/agentic_wm_admin.py).
    // Opening a conversation is logged server-side, so the thread query is
    // lazy - it runs only when a reviewer clicks one.
    getAgenticWmUsage: builder.query({
      query: (days = 30) => ({ url: '/admin/agentic-wm/usage', params: { days } }),
    }),
    listAgenticWmAllThreads: builder.query({
      query: (params = {}) => ({ url: '/admin/agentic-wm/threads', params: cleanParams(params) }),
    }),
    getAgenticWmReviewThread: builder.query({
      query: (id) => `/admin/agentic-wm/threads/${id}`,
    }),
    listAgenticWmFeedback: builder.query({
      query: () => '/admin/agentic-wm/feedback',
    }),
    listAgenticWmViews: builder.query({
      query: () => '/admin/agentic-wm/views',
    }),

    // ── Net New AUM (Work side) ────────────────────────────────────────────
    // Reads the local `orion_flows` cache, not Orion directly — a Query
    // Designer run is allowed twenty minutes by Orion, which is not a page
    // load. Firm and advisor come from the FSC household join, so these are
    // scoped by the same office rules as the client book.
    getAumSummary: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: '/work/aum/summary', params: clean };
      },
    }),
    getAumTrend: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: '/work/aum/trend', params: clean };
      },
    }),
    // On/off-platform split of the counted net flow. Off-platform is AssetMark,
    // Orion Portfolio Solutions and Mutual Securities (Evolve), read from the
    // management style Orion stamps on every transaction — so a closed
    // account's money is still placed, which an account-list join cannot do.
    // `unknown` is returned rather than folded into on-platform, and is 100%
    // until a live sync has repopulated the window.
    getAumPlatform: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: '/work/aum/platform', params: clean };
      },
    }),
    // Counted net flow by Orion management style, beside the internal journals
    // (types 171/172) that sleeving moves money with. New money is coded
    // `Unassigned` until someone decides where it goes and `Sleeved Custodial
    // Account` once it is sleeved, so a report grouping RAW movements by style
    // shows the arrival positive and the sleeving negative and the new money
    // looks like it left. It did not: the journals are excluded, so both styles
    // come out positive on counted flow. The journal columns are returned so
    // that can be seen rather than asserted.
    getAumStyles: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: '/work/aum/styles', params: clean };
      },
    }),
    getAumFirmAdvisors: builder.query({
      query: (p) => ({
        url: `/work/aum/firms/${p.officeId}`,
        params: { start: p.start, end: p.end },
      }),
    }),
    listAumFirms: builder.query({
      query: () => '/work/aum/firms',
    }),
    getAumStatus: builder.query({
      query: () => '/work/aum/status',
    }),
    // Household-level breakdown behind an advisor row: counted flows beside the
    // excluded buckets (merges, journals, tax). The pair that matters is
    // merge_in against client_start_date — PCA uses sleeved accounts, Orion
    // models each sleeve as its own account, so a new client's transferred-in
    // book arrives as a MERGE and not a contribution.
    getAumHouseholds: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: '/work/aum/households', params: clean };
      },
    }),
    // The same rows as a file. `responseHandler` returns the raw blob — the
    // default JSON handler would choke on CSV — and going through RTK rather
    // than a bare link is what puts the bearer token on the request.
    getAumHouseholdsCsv: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return {
          url: '/work/aum/households.csv',
          params: clean,
          responseHandler: (response) => response.blob(),
          cache: 'no-cache',
        };
      },
    }),
    // Super Admin -> Config -> Flow types. The definition of net new AUM: which
    // Orion transaction types are counted, which are excluded (and why), and
    // which are never fetched at all. Was half config rows and half a hardcoded
    // dict, so changing an exclusion needed a deploy.
    getOrionFlowTypes: builder.query({
      query: () => ({ url: '/admin/orion-flow-types' }),
    }),
    // Saving re-applies the new definition to rows already stored and rebuilds
    // the rollup — seconds, and it never touches Orion, which is the whole point
    // of keeping `orion_transactions` at transaction grain. The exception is a
    // type leaving `ignore`: nothing was ever fetched for it, so the response
    // names those in `needs_sync` and the page says a sync is required.
    setOrionFlowTypes: builder.mutation({
      query: (p) => ({
        url: '/admin/orion-flow-types',
        method: 'PUT',
        body: { updates: p.updates, apply: p.apply !== false },
      }),
    }),
    // One household's transactions read LIVE FROM ORION, with the treatment our
    // rules give each type. The only Net New AUM read that bypasses the cache,
    // because the cache cannot audit itself: the sync stores 12 of Orion's 51
    // fields and fetches only the types already on our lists, so the evidence
    // for "is this exclusion right" (the custodian's own notes, the ACAT file
    // code, Orion's pairing key) is in the part we drop. Pass `type_id` to also
    // get that type's raw rows.
    getAumHouseholdOrion: builder.query({
      query: (params = {}) => {
        const { household_id: id, ...rest } = params;
        const clean = {};
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: `/work/aum/households/${encodeURIComponent(id)}/orion`, params: clean };
      },
    }),
    // Households whose flows we count but Salesforce has no record of. Since
    // attribution moved to Orion's office these are IN the totals, which is only
    // safe if they are also on the screen — each one is an unfinished
    // onboarding, not a rounding difference. Fetched lazily behind the banner.
    getAumNotInFsc: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: '/work/aum/not-in-fsc', params: clean };
      },
    }),
    // How the headline is built — every Orion transaction type counted and every
    // one left out, priced. Fetched lazily: it is the answer to "what did you
    // include?", not something every page load needs.
    getAumBreakdown: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return { url: '/work/aum/breakdown', params: clean };
      },
    }),
    // Mint a ONE-TIME link for a migrated/S3-backed document. The viewer cannot
    // fetch /api/documents/{doc_id} directly — fetchAsBlobUrl sends no auth
    // header, so that path 401s. Same two-step the client-document viewer uses.
    getMigratedDocumentUrl: builder.mutation({
      query: (p) => ({ url: `/documents/${p.docId}/download_url`, method: 'GET' }),
    }),

    // ── Compliance Quick Hitter (monthly compliance bulletin) ──────────────
    // `current` drives the hub's Quick Links tile; it returns null until
    // Compliance publish an issue, so the tile hides the link rather than
    // opening an empty modal.
    // NOTE the tags below are inert — this slice declares no `tagTypes` (see the
    // comment above listB2BAnnouncements), so the admin page calls refetch()
    // explicitly after every mutation rather than relying on invalidation.
    getCurrentComplianceBulletin: builder.query({
      query: () => "/compliance-bulletins/current",
      providesTags: ['ComplianceBulletins'],
    }),
    listComplianceBulletins: builder.query({
      query: () => "/compliance-bulletins",
      providesTags: ['ComplianceBulletins'],
    }),
    // A mutation despite being a GET: the content is fetched on demand when the
    // modal opens, and shouldn't be cached with the tile metadata.
    getComplianceBulletinContent: builder.mutation({
      query: (p) => ({ url: `/compliance-bulletins/${p.id}/content`, method: "GET" }),
    }),
    uploadComplianceBulletin: builder.mutation({
      // body is a FormData with `file`, `title`, `sort_date`, and optionally
      // `period_label` / `published`.
      query: (p) => ({ url: "/compliance-bulletins", method: "POST", body: p.body, formData: true }),
      invalidatesTags: ['ComplianceBulletins'],
    }),
    updateComplianceBulletin: builder.mutation({
      query: (p) => ({ url: `/compliance-bulletins/${p.id}`, method: "PATCH", body: p.body }),
      invalidatesTags: ['ComplianceBulletins'],
    }),
    deleteComplianceBulletin: builder.mutation({
      query: (p) => ({ url: `/compliance-bulletins/${p.id}`, method: "DELETE" }),
      invalidatesTags: ['ComplianceBulletins'],
    }),

    // ── Marketing Resources ────────────────────────────────────────────────
    // The collateral library published to B2B entrepreneur firms. Two employee
    // surfaces: a read-only view for everyone (/marketing/library) and the
    // MARKETING-gated management view + activity report (/marketing/admin/*).
    // The B2B portal talks to its own /b2b/marketing/* endpoints through
    // b2bFetch, not through this slice — b2b/ deliberately imports nothing from
    // the intranet.
    //
    // Tags are inert here as everywhere in this slice (no `tagTypes` declared —
    // see the note above listB2BAnnouncements), so the pages refetch() after
    // every mutation.
    getMarketingLibrary: builder.query({
      query: () => "/marketing/library",
    }),
    // A mutation despite being a GET: the presigned URL is minted per click and
    // expires in five minutes, so caching it alongside the library would hand
    // someone a dead link. It also RECORDS the open, which is the second reason
    // it must never be cached — a cached response is an uncounted click.
    getMarketingResourceUrl: builder.mutation({
      query: (p) => ({
        url: `/marketing/resources/${p.id}/download_url`,
        method: "GET",
        params: p.disposition ? { disposition: p.disposition } : undefined,
      }),
    }),
    // Link opens go through the API rather than an <a href> so the click can be
    // counted — almost every asset is an external Box link, and a browser
    // navigating straight there is invisible to us. Same no-cache reasoning.
    openMarketingResource: builder.mutation({
      query: (p) => ({ url: `/marketing/resources/${p.id}/open`, method: "GET" }),
    }),
    openMarketingGroup: builder.mutation({
      query: (p) => ({ url: `/marketing/groups/${p.id}/open`, method: "GET" }),
    }),

    getMarketingAdminLibrary: builder.query({
      query: () => "/marketing/admin/library",
    }),
    createMarketingCategory: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/categories", method: "POST", body }),
    }),
    updateMarketingCategory: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/categories/${p.id}`, method: "PATCH", body: p.body }),
    }),
    deleteMarketingCategory: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/categories/${p.id}`, method: "DELETE" }),
    }),
    reorderMarketingCategories: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/categories/reorder", method: "PUT", body }),
    }),
    // Groups are the middle level: one guide, one presentation, one month, one
    // discount partner. Deleting one does NOT delete its items — they come back
    // as ungrouped rows at the top of the section (the response says how many).
    createMarketingGroup: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/groups", method: "POST", body }),
    }),
    updateMarketingGroup: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/groups/${p.id}`, method: "PATCH", body: p.body }),
    }),
    deleteMarketingGroup: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/groups/${p.id}`, method: "DELETE" }),
    }),
    reorderMarketingGroups: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/groups/reorder", method: "PUT", body }),
    }),
    createMarketingLink: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resources/link", method: "POST", body }),
    }),
    // A label row inside a group — no link, no file. The source pages use these
    // to break a twenty-item presentation into readable chunks.
    createMarketingHeading: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resources/heading", method: "POST", body }),
    }),
    // File uploads are a two-step ticket → commit; the BYTES never come through
    // this API. `upload-ticket` returns a presigned POST that the browser sends
    // straight to the pca-marketing bucket, which is what lets a 90 MB deck
    // through at all — nginx caps request bodies at 26 MB. `commit` then HEADs
    // the object and catalogues it.
    getMarketingUploadTicket: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resources/upload-ticket", method: "POST", body }),
    }),
    commitMarketingUpload: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resources/commit", method: "POST", body }),
    }),
    updateMarketingResource: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resources/${p.id}`, method: "PATCH", body: p.body }),
    }),
    deleteMarketingResource: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resources/${p.id}`, method: "DELETE" }),
    }),
    reorderMarketingResources: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resources/reorder", method: "PUT", body }),
    }),

    // Activity report (MARKETING role). Every call takes the same window and the
    // same two "count PCA as a customer?" switches, both defaulting to false
    // server-side: include_employees (our own staff browsing the internal view)
    // and include_impersonated (clicks made inside a "log in as" session).
    getMarketingActivitySummary: builder.query({
      query: (params) => ({ url: "/marketing/admin/activity/summary", params }),
    }),
    getMarketingActivityFirms: builder.query({
      query: (params) => ({ url: "/marketing/admin/activity/firms", params }),
    }),
    // Fetched per firm only when a row is expanded — a page of forty firms
    // shouldn't pull forty member lists nobody asked to see.
    getMarketingActivityMembers: builder.query({
      query: (p) => ({ url: `/marketing/admin/activity/firms/${p.firm_id}/members`, params: p.params }),
    }),
    getMarketingActivityItems: builder.query({
      query: (params) => ({ url: "/marketing/admin/activity/items", params }),
    }),
    // The raw event feed as a file, because this is what gets pivoted. Same
    // shape as getAumHouseholdsCsv: `responseHandler` returns the raw blob (the
    // default JSON handler would choke on CSV), and going through RTK rather
    // than a bare <a href> is what puts the bearer token on the request.
    getMarketingActivityCsv: builder.query({
      query: (params = {}) => {
        const clean = {};
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') clean[k] = v;
        });
        return {
          url: "/marketing/admin/activity/export.csv",
          params: clean,
          responseHandler: (response) => response.blob(),
          cache: "no-cache",
        };
      },
    }),

    // ── Resource Library ───────────────────────────────────────────────────
    // The internal reference pages the MARKETING team author, sitting alongside the
    // nested Marketing library under Desk → Resource Library. Two levels of CONTENT
    // (page → item), and four item kinds — FILE and VIDEO are uploads, LINK and
    // EMBED are URLs — all DERIVED server-side rather than picked, so the page
    // just reads `kind` and renders accordingly.
    //
    // The payload also carries `groups`: sidebar folders holding pages, with a
    // name, an icon and a position and no content of their own. A page's
    // `group_id` says which folder it hangs under, or null for a leaf sitting
    // directly under Resource Library as every page did before folders existed.
    // The server has ALREADY dropped any folder holding none of this reader's
    // pages, so the menu renders what it is given and never has to ask whether a
    // folder would unfold onto nothing.
    //
    // These endpoints are the EMPLOYEE surface. They return the pages ticked
    // "Visible to employees" — plus, for a caller holding the BETA role, the
    // ones ticked "Visible to beta testers" but not yet released. THE SERVER
    // DECIDES THAT from the caller's roles (read from the DB, not the token), so
    // nothing here passes a flag and nothing here filters: a page admitted only
    // by the beta tick arrives carrying `beta_preview: true`, which is what the
    // reader page badges.
    //
    // A page can also be published to the B2B firms, but the portal reads that
    // through its own /api/b2b/resource-library/* calls in b2b/Dashboard.jsx —
    // b2b/ imports nothing from this slice, and that read path never looks at
    // the beta flag.
    //
    // Tags are inert here as everywhere in this slice (no `tagTypes` declared),
    // so the pages refetch() after every mutation.
    getResourcePages: builder.query({
      query: () => "/resource-library/pages",
    }),
    // A mutation despite being a GET, for the same reason as the marketing
    // download: the presigned URL is minted per click and expires, so caching it
    // alongside the page would eventually hand someone a dead link.
    //
    // `disposition: 'inline'` is what the video player and PDF preview ask for
    // and it also buys a much longer TTL server-side — a 5-minute URL dies
    // mid-playback, because every range request re-checks the signature.
    getResourceItemFileUrl: builder.mutation({
      query: (p) => ({
        url: `/resource-library/items/${p.id}/file_url`,
        method: "GET",
        params: p.disposition ? { disposition: p.disposition } : undefined,
      }),
    }),

    // MARKETING management surface. Same role as the Marketing Resources
    // endpoints above, and deliberately a sibling of them under
    // /marketing/admin/ — but a different feature over different tables: these
    // pages are internal-first, those categories are customer collateral.
    // Returns pages AND groups, drafts and empty folders included — the admin
    // view needs a folder to exist before pages can be moved into it, so this is
    // the one surface where a group with no pages comes back.
    getResourceAdminPages: builder.query({
      query: () => "/marketing/admin/resource-library/pages",
    }),
    // Sidebar folders. No read endpoint of their own — they arrive in the page
    // payloads above, already narrowed per audience by the server.
    createResourceGroup: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/groups", method: "POST", body }),
    }),
    updateResourceGroup: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resource-library/groups/${p.id}`, method: "PATCH", body: p.body }),
    }),
    deleteResourceGroup: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resource-library/groups/${p.id}`, method: "DELETE" }),
    }),
    reorderResourceGroups: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/groups/reorder", method: "PUT", body }),
    }),
    createResourcePage: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/pages", method: "POST", body }),
    }),
    updateResourcePage: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resource-library/pages/${p.id}`, method: "PATCH", body: p.body }),
    }),
    deleteResourcePage: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resource-library/pages/${p.id}`, method: "DELETE" }),
    }),
    reorderResourcePages: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/pages/reorder", method: "PUT", body }),
    }),
    // One endpoint for a website link AND a YouTube/Vimeo video: which one it
    // becomes depends on the URL, and the server decides (see _embed_url in
    // routers/resource_library.py). The admin page says so rather than offering
    // a "type" dropdown that could disagree with what was pasted.
    createResourceLink: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/items/link", method: "POST", body }),
    }),
    // Uploads are a two-step ticket → commit; the BYTES never come through this
    // API. `upload-ticket` returns a presigned POST the browser sends straight
    // to pca-marketing/resource-library/<page>/, which is what lets a 200 MB
    // training video through at all — nginx caps request bodies at 26 MB.
    // `commit` then HEADs the object, decides FILE vs VIDEO from what S3 stored,
    // and catalogues it.
    getResourceUploadTicket: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/items/upload-ticket", method: "POST", body }),
    }),
    commitResourceUpload: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/items/commit", method: "POST", body }),
    }),
    updateResourceItem: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resource-library/items/${p.id}`, method: "PATCH", body: p.body }),
    }),
    deleteResourceItem: builder.mutation({
      query: (p) => ({ url: `/marketing/admin/resource-library/items/${p.id}`, method: "DELETE" }),
    }),
    reorderResourceItems: builder.mutation({
      query: (body) => ({ url: "/marketing/admin/resource-library/items/reorder", method: "PUT", body }),
    }),

    // "Your Day" morning briefing. A query (not a mutation) despite the POST, so
    // RTK Query caches the result for the session — the page generates on mount,
    // and without a cache every navigation back to it would re-read the mailbox
    // and re-bill Bedrock. Refresh is the hook's refetch().
    //
    // The delegated Graph token rides in the body because prepareHeaders owns the
    // `authorization` header (that's the hub JWT). It also rotates on every
    // acquireTokenSilent, so it's excluded from the cache key via
    // serializeQueryArgs — otherwise every render would look like a new query and
    // the cache would never hit.
    // Meetings Assistant (beta). Same token-in-body / stable-cache-key reasoning
    // as the Your Day endpoints below: the delegated Graph token rotates on every
    // acquireTokenSilent, so it must not participate in the cache key.
    // Role Alignment ⇄ Managerial Assessment. team_status is open to any
    // employee: someone with no reports gets an empty list, so the page can
    // decide whether to show itself.
    // The caller's own submission, so a completed form shows their answers back
    // instead of a blank form they could accidentally fill in twice.
    // In-progress answers, so a long form can be finished another day on another
    // device. A draft is readable only by its author — there is no endpoint that
    // returns anyone else's, by design — and lives in its own table so nothing
    // that reads submitted forms can ever see one.
    getFormDraft: builder.query({
      query: (p) => ({
        url: `/forms/${p.form_key}/draft`,
        params: p.subjectDirectoryId ? { subject_directory_id: p.subjectDirectoryId } : undefined,
      }),
    }),

    // Autosaved on a debounce and by an explicit "Save & finish later". No
    // providesTags/invalidatesTags: this slice declares no tagTypes, and the form
    // holds the answers in component state anyway — a refetch mid-typing would be
    // actively harmful.
    saveFormDraft: builder.mutation({
      query: (p) => ({
        url: `/forms/${p.form_key}/draft`,
        method: "PUT",
        body: { responses: p.responses, subject_directory_id: p.subjectDirectoryId || null },
      }),
    }),

    deleteFormDraft: builder.mutation({
      query: (p) => ({
        url: `/forms/${p.form_key}/draft`,
        method: "DELETE",
        params: p.subjectDirectoryId ? { subject_directory_id: p.subjectDirectoryId } : undefined,
      }),
    }),

    getMyFormSubmission: builder.query({
      query: (p) => `/forms/${p.form_key}/my_submission`,
    }),

    getFormsTeamStatus: builder.query({
      // No providesTags: this slice declares no tagTypes, so tags are inert
      // app-wide — the Team page refetch()es explicitly after a submission.
      query: () => "/forms/team_status",
    }),

    // A direct report's questionnaire, for their direct manager only (404 for
    // anyone else, deliberately, so it can't be used to map the org chart).
    getReportRoleAlignment: builder.query({
      query: (p) => `/forms/report/${p.directoryId}/role_alignment`,
    }),

    submitManagerialAssessment: builder.mutation({
      query: (p) => ({
        url: "/forms/managerial_assessment/submit",
        method: "POST",
        body: { subject_directory_id: p.subjectDirectoryId, responses: p.responses },
      }),
    }),

    // Planner summary (beta). Same token-in-body / stable-cache-key reasoning as
    // the Your Day and Meetings endpoints. Cached for the session because the
    // call is expensive in a way the others aren't: it fans out over every plan
    // the person belongs to, against a service that throttles readily, so a
    // navigation back to the page must not re-read all of it. Refresh is the
    // hook's own trigger with preferCache false.
    // Move targets for the Your Day "File to…" control. Cached for the session
    // (folders barely change) and keyed stably like the other token-in-body
    // endpoints. Fetched lazily on first use of the control, so nobody who never
    // files mail pays a Graph call — or triggers the write-scope consent prompt.
    getMailFolders: builder.query({
      query: (p) => ({
        url: "/your_day/mail_folders",
        method: "POST",
        body: { graph_token: p.graphToken, mailbox: p.mailbox || null },
      }),
      // Keyed by mailbox: folder trees are per-mailbox, and a stable key would
      // offer service@'s folders as move targets for your own mail.
      serializeQueryArgs: ({ queryArgs }) => `mailFolders:${queryArgs?.mailbox || "me"}`,
      keepUnusedDataFor: 600,
    }),

    // A real mutation: it changes a mailbox. Deliberately does NOT invalidate the
    // briefing — a refetch would re-read the inbox and re-bill Bedrock for a
    // whole new briefing over one filed message. The page drops the row locally
    // instead, using the new message id this returns.
    moveMessage: builder.mutation({
      query: (p) => ({
        url: "/your_day/move_message",
        method: "POST",
        body: {
          graph_token: p.graphToken,
          message_id: p.messageId,
          destination_id: p.destinationId,
          // Must be the mailbox the message id came from: a Graph message id
          // only resolves in its own mailbox, so the wrong one 404s rather than
          // misfiling anything.
          mailbox: p.mailbox || null,
        },
      }),
    }),

    // "I'm done with this one" — hub-side only, so no Graph token and no
    // mailbox write. Deliberately does NOT invalidate the briefing, for the same
    // reason moveMessage doesn't: a refetch would re-read the inbox and re-bill
    // Bedrock over one dismissed row. The page hides it locally; the freed slot
    // appears in the next briefing.
    dismissBriefingMessage: builder.mutation({
      query: (p) => ({
        url: "/your_day/dismiss",
        method: "POST",
        body: { message_id: p.messageId },
      }),
    }),
    undismissBriefingMessage: builder.mutation({
      query: (p) => ({
        url: "/your_day/undismiss",
        method: "POST",
        body: { message_id: p.messageId },
      }),
    }),

    // Ask a question across the submitted Role Alignment forms. The CORPUS goes
    // up with the question: the server has no copy of the form schema, so it
    // cannot turn `co_pct` and `hats_shed` back into questions — the page that
    // renders those labels sends them, having just fetched the answers through
    // fetchRoleAlignmentResponses. CONFIDENTIAL_REVIEWER on both ends.
    askRoleAlignment: builder.mutation({
      query: (body) => ({ url: "/forms/role_alignment/ask", method: "POST", body }),
    }),

    getPlannerSummary: builder.query({
      query: (p) => ({
        url: "/planner/summary",
        method: "POST",
        body: { graph_token: p.graphToken },
      }),
      serializeQueryArgs: () => "plannerSummary",
      keepUnusedDataFor: 900,
    }),

    getMeetingsAgenda: builder.query({
      query: (p) => ({
        url: "/meetings/agenda",
        method: "POST",
        body: { graph_token: p.graphToken, days: p.days || 7 },
      }),
      serializeQueryArgs: () => "meetingsAgenda",
      keepUnusedDataFor: 300,
    }),

    // A brief is generated per (event, depth) and stored, so this is a mutation
    // rather than a query — pressing "Ramp me up" again with refresh spends model
    // tokens deliberately.
    buildMeetingBrief: builder.mutation({
      query: (p) => ({
        url: "/meetings/brief",
        method: "POST",
        body: {
          graph_token: p.graphToken,
          event_id: p.eventId,
          emails: p.emails || [],
          depth: p.depth || "quick",
          refresh: !!p.refresh,
        },
      }),
    }),

    // The mailboxes this person can pick between — every shared and delegated
    // mailbox Outlook lets them open, discovered rather than configured. Cheap:
    // a cached read server-side, so the page can wait on it.
    getYourDayMailboxes: builder.query({
      query: (p) => ({
        url: "/your_day/mailboxes",
        method: "POST",
        body: { graph_token: p.graphToken },
      }),
      serializeQueryArgs: () => "yourDayMailboxes",
      keepUnusedDataFor: 1800,
    }),

    // The expensive half: probes every mailbox in the tenant with this person's
    // token to find what they can open (~22 batched Graph calls). A mutation
    // because it writes the scan cache, and fired in the BACKGROUND — the page
    // renders from the cached menu and swaps in the result when it lands, so
    // nobody waits on it. Only runs when the menu says `needs_scan`, or when
    // somebody presses Rescan.
    scanYourDayMailboxes: builder.mutation({
      query: (p) => ({
        url: "/your_day/mailboxes/scan",
        method: "POST",
        body: { graph_token: p.graphToken },
      }),
    }),

    // Adding and removing entries on the person's OWN menu. Mutations because
    // they write, but neither invalidates a tag — tagTypes are inert app-wide,
    // so the page refetches the menu itself once the change lands.
    addYourDayMailbox: builder.mutation({
      query: (p) => ({
        url: "/your_day/mailboxes/add",
        method: "POST",
        body: { address: p.address, label: p.label || null, graph_token: p.graphToken },
      }),
    }),
    removeYourDayMailbox: builder.mutation({
      query: (p) => ({
        url: "/your_day/mailboxes/remove",
        method: "POST",
        body: { address: p.address },
      }),
    }),

    // Keyed BY MAILBOX rather than with a stable key. That's what makes the
    // mailbox menu cheap to use: each mailbox's briefing is cached separately, so
    // clicking back to one already generated is instant and costs no Bedrock
    // call. A single stable key would have made every switch a regeneration —
    // and with a default of 6 briefings per person per hour, four clicks would
    // have hit the limit.
    getYourDayBriefing: builder.query({
      query: (p) => ({
        url: "/your_day/briefing",
        method: "POST",
        body: { graph_token: p.graphToken, mailbox: p.mailbox || null },
      }),
      serializeQueryArgs: ({ queryArgs }) => `yourDayBriefing:${queryArgs?.mailbox || "me"}`,
      keepUnusedDataFor: 900,
    }),

    // Week in Review — same token-in-body / per-mailbox-cache-key reasoning as
    // the briefing above. Cached far longer (1h): it summarizes seven days, so
    // its content barely moves within a working session, and it's the more
    // expensive of the two calls.
    getYourDayWeekReview: builder.query({
      query: (p) => ({
        url: "/your_day/week_review",
        method: "POST",
        body: { graph_token: p.graphToken, mailbox: p.mailbox || null },
      }),
      serializeQueryArgs: ({ queryArgs }) => `yourDayWeekReview:${queryArgs?.mailbox || "me"}`,
      keepUnusedDataFor: 3600,
    }),

    // Bloom Growth rocks and to-dos — same token-in-body / stable-cache-key
    // reasoning as the two above. Its own query rather than part of the
    // briefing: Bloom is a third-party API, so if it's slow or down the morning
    // briefing must still render.
    getYourDayBloom: builder.query({
      query: (p) => ({
        url: "/your_day/bloom",
        method: "POST",
        body: { graph_token: p.graphToken },
      }),
      serializeQueryArgs: () => "yourDayBloom",
      keepUnusedDataFor: 900,
    }),

    // The only two writes the hub makes to Bloom. Not symmetrical, because
    // Bloom's API isn't: rocks have an archive endpoint, to-dos have only
    // "complete". Neither invalidates a tag — tagTypes are inert app-wide, so
    // the page refetches the tile itself once the change lands.
    archiveBloomRock: builder.mutation({
      query: (p) => ({
        url: "/your_day/bloom/archive_rock",
        method: "POST",
        body: { graph_token: p.graphToken, rock_id: p.rockId },
      }),
    }),
    completeBloomTodo: builder.mutation({
      query: (p) => ({
        url: "/your_day/bloom/complete_todo",
        method: "POST",
        body: { graph_token: p.graphToken, todo_id: p.todoId },
      }),
    }),
    // Lazy: fetched only when someone opens the "Archived rocks" list, which on
    // most mornings is nobody. POST like its neighbours because the delegated
    // token rides in the body.
    getArchivedBloomRocks: builder.query({
      query: (p) => ({
        url: "/your_day/bloom/archived_rocks",
        method: "POST",
        body: { graph_token: p.graphToken },
      }),
      serializeQueryArgs: () => "archivedBloomRocks",
    }),
    restoreBloomRock: builder.mutation({
      query: (p) => ({
        url: "/your_day/bloom/restore_rock",
        method: "POST",
        body: { graph_token: p.graphToken, rock_id: p.rockId },
      }),
    }),

    // One typed question, answered from the person's own mail, calendar and
    // Slack. A MUTATION rather than a query, deliberately: it is an action
    // somebody takes, it costs AI tokens, and it must never be re-fired by a
    // cache refetch or a remount the way a query can be.
    askGlance: builder.mutation({
      query: (p) => ({
        url: "/your_day/ask",
        method: "POST",
        body: {
          graph_token: p.graphToken,
          mailbox: p.mailbox || null,
          question: p.question,
        },
      }),
    }),

    // ── Slack connection (At A Glance) ──
    // Plain authenticated calls, no Graph token: these are about the person's
    // SLACK account, and Outlook has nothing to do with it. The Slack items
    // themselves arrive inside the briefing and week-review payloads, tiered
    // alongside the mail — there is deliberately no "fetch my Slack" endpoint,
    // because a second list would be the thing the merge exists to avoid.
    getSlackStatus: builder.query({
      query: () => ({ url: "/slack/status" }),
      serializeQueryArgs: () => "slackStatus",
    }),
    // Returns Slack's consent URL for the page to open. A 302 here would be
    // followed by fetch and the person would never see the consent screen.
    connectSlack: builder.mutation({
      query: () => ({ url: "/slack/connect", method: "POST" }),
    }),
    disconnectSlack: builder.mutation({
      query: () => ({ url: "/slack/disconnect", method: "DELETE" }),
    }),

    // ── Calendar (shared Outlook calendars) ──
    // POST for the reads too: the delegated Graph token rides in the body,
    // because prepareHeaders owns the `authorization` header (that's the hub JWT).
    getCalendars: builder.query({
      query: (p) => ({ url: "/calendar/calendars", method: "POST", body: { graph_token: p.graphToken } }),
      serializeQueryArgs: () => "calendars",
    }),
    getCalendarEvents: builder.query({
      query: (p) => ({
        url: "/calendar/events",
        method: "POST",
        params: { calendar_ids: p.calendarIds, start: p.start, days: p.days },
        body: { graph_token: p.graphToken },
      }),
      // The token rotates on every acquire, so it must not be part of the cache
      // key — but the window and calendar selection must be.
      serializeQueryArgs: ({ queryArgs }) =>
        `events:${queryArgs.calendarIds}:${queryArgs.start}:${queryArgs.days}`,
    }),
    createCalendarEvent: builder.mutation({
      query: (p) => ({ url: "/calendar/events/create", method: "POST", body: p }),
    }),
    updateCalendarEvent: builder.mutation({
      query: ({ eventId, ...body }) => ({ url: `/calendar/events/${encodeURIComponent(eventId)}`, method: "PATCH", body }),
    }),
    inviteCalendarAttendees: builder.mutation({
      query: ({ eventId, ...body }) => ({ url: `/calendar/events/${encodeURIComponent(eventId)}/invite`, method: "POST", body }),
    }),
    deleteCalendarEvent: builder.mutation({
      query: ({ eventId, ...body }) => ({ url: `/calendar/events/${encodeURIComponent(eventId)}/delete`, method: "POST", body }),
    }),

    // Per-employee preferences (time zone), set from the sidebar gear.
    getMyPreferences: builder.query({
      query: () => ({ url: "/preferences" }),
    }),
    setMyTimezone: builder.mutation({
      query: (p) => ({
        url: "/preferences/timezone",
        method: "PUT",
        body: { timezone: p.timezone ?? "" },
      }),
    }),

    // ── Tasks (Microsoft To Do + Planner) ────────────────────────────────
    //
    // Deliberately NOT cached. Every other Graph-backed query here keeps its
    // result for a while because a briefing is expensive and doesn't change
    // minute to minute. Tasks is the opposite: it's a page people work out of,
    // the data is authoritative in M365, and a cached list is a list that
    // disagrees with the phone in their hand. `keepUnusedDataFor: 0` plus
    // refetch-after-write is what keeps the hub honest about being a live view.
    getTaskSources: builder.query({
      query: (p) => ({
        url: "/tasks/sources",
        method: "POST",
        body: { graph_token: p.graphToken },
      }),
      serializeQueryArgs: () => "taskSources",
      keepUnusedDataFor: 0,
    }),

    getTaskItems: builder.query({
      query: (p) => ({
        url: "/tasks/items",
        method: "POST",
        body: {
          graph_token: p.graphToken,
          source: p.source,
          source_id: p.sourceId,
          include_completed: Boolean(p.includeCompleted),
        },
      }),
      // Keyed by the source, so switching lists doesn't show the previous
      // list's tasks for a frame — which on a page with checkboxes would
      // invite someone to tick the wrong thing.
      serializeQueryArgs: ({ queryArgs }) =>
        `taskItems:${queryArgs.source}:${queryArgs.sourceId}:${queryArgs.includeCompleted ? 1 : 0}`,
      keepUnusedDataFor: 0,
    }),

    resolveTaskPeople: builder.query({
      query: (p) => ({
        url: "/tasks/people",
        method: "POST",
        body: { graph_token: p.graphToken, emails: p.emails || [] },
      }),
      keepUnusedDataFor: 300,
    }),

    createTask: builder.mutation({
      query: (p) => ({
        url: "/tasks/create",
        method: "POST",
        body: {
          graph_token: p.graphToken,
          source: p.source,
          source_id: p.sourceId,
          title: p.title,
          due: p.due || null,
          note: p.note || null,
          assignee_emails: p.assigneeEmails || null,
          bucket_id: p.bucketId || null,
        },
      }),
    }),

    setTaskCompleted: builder.mutation({
      query: (p) => ({
        url: "/tasks/complete",
        method: "POST",
        body: {
          graph_token: p.graphToken,
          source: p.source,
          source_id: p.sourceId,
          task_id: p.taskId,
          completed: Boolean(p.completed),
        },
      }),
    }),

  })

});

// Export the auto-generated hooks for the endpoints
export const {
  useGetFscSyncQuery,
  useTriggerFscSyncMutation,
  useGetOrionSyncQuery,
  useTriggerOrionSyncMutation,
  useTriggerOrionTypeCensusMutation,
  useSetOrionSyncEnabledMutation,
  useGetCrmSyncQuery,
  useRunCrmSyncMutation,
  useGetCrmSyncOptionsQuery,
  useSaveCrmSourceMutation,
  useRunCrmMatchMutation,
  useUploadCrmExportMutation,
  useGetCrmMigrationQuery,
  useRunCrmMigrationStepMutation,
  useLazyGetCrmMigrationLogQuery,
  useGetNonClientsQuery,
  useAddNonClientMutation,
  useDeleteNonClientMutation,
  useGetBloomBoardQuery,
  useGetBloomUsersQuery,
  usePinBloomUserMutation,
  useGetYourDayBloomQuery,
  useLazyGetYourDayBloomQuery,
  useArchiveBloomRockMutation,
  useCompleteBloomTodoMutation,
  useLazyGetArchivedBloomRocksQuery,
  useRestoreBloomRockMutation,
  useGetWorkScopesQuery,
  useSetOfficeKindMutation,
  useSetUserScopeMutation,
  useSetUserWorkRolesMutation,
  useGetFirmOfficesQuery,
  useMapFirmOfficeMutation,
  useSetFirmDisplayNameMutation,
  useGetHealthQuery, 
  useLaunchKYStartMutation, useLaunchKYFromInsideMutation,
  useLiveStreamModelCountsQuery, useLiveStreamsQuery,
  useCreateUserMutation, useLoginMutation, useAdminLoginMutation,
  useAdminRoleMutation, useEnable2faMutation, useCheck2faCodeMutation, useLoginCodeValidMutation,
  useUploadS3Mutation, useContestImagesQuery,
  useCastVoteMutation, useRemoveVoteMutation, useCountUserVotesQuery,
  useDeleteImageMutation, useEditImageMutation,
  useSendEmailMutation, useCheckCodeMutation,useGetConfigQuery,
  useGetAdminConfigQuery, useSetAdminConfigMutation, useSearchDocumentAccessLogQuery, useSearchLoginAttemptsQuery,
  useGetEmployeeUsageNotifyQuery, useSetEmployeeUsageNotifyMutation,
  useGetSharedMailboxesQuery, useSetSharedMailboxesMutation,
  useGetCeleryScheduleQuery, useGetCeleryWorkersQuery,
  useLazyGetDocusignStatusQuery, useLazyGetDocusignConsentUrlQuery,
  useLazyGetDocusignUserinfoQuery, useLazyDocusignWarmupQuery,
  useLazyListDocusignTemplatesQuery, useLazyInspectDocusignTemplateQuery,
  useGetKYBQuery, useGetKYBImagesMutation, useUploadKYBS3Mutation, 
  useGetAllPartnersQuery, useUpdatePartnerMutation,
  useGetVerifyAccountCountsQuery, useGetVerifyAccountsByTypeQuery,
  useGetImagesMutation, useUpdateVerifyAccountStatusMutation,
  useGetAllMemberStatsQuery, useGetAllMembersForMutation,
  useGetStudioCountsQuery, useSearchMutation,
  useEditVerifyStatusMutation, useGetStatusHistoryMutation, useUploadExcelMutation,
  useGetPastAMLRunsQuery, useStartAccountUpdateMutation,
  useGetAMLBusinessHistoryQuery, useGetAMLIndividualHistoryQuery,
  useRunSingleAmlMutation, useGetFailedAccountsMutation,
  useGetPresignedURLMutation,   useGetKYBResultByAccountAndTypeMutation,
  useGetAllNotesQuery, useAddNoteMutation, useEditNoteMutation,
  useGetMentionableUsersQuery, usePostKYCSentMutation,
  useRunGetQuery, useRunPostMutation, useRunPostWithBodyMutation,
  useGetKYBResultsByPaymentStatusQuery, useGetIDVResultsWithSummaryQuery,
  useGetConfirmIDSummaryQuery, useGetConfirmIDDailyCountsQuery, useLazyGetConfirmIDImagesQuery,
  useGetRekognitionSummaryQuery, useGetRekognitionDailyCountsQuery,
  useUpdateProfileAssignmentMutation, useUpdateUserAssignmentMutation, useUpdateCompanyNameMutation,
  useCaptureContractMutation, useCaptureContractFromHtmlMutation,
  useGetAdminUsersQuery, useUpdateAdminRoleMutation, useGetKYCSentsMutation,
  useKybFormSubmissionMutation, useSendKYCEmailMutation, usePostAutoAMLMutation,
  useLazyExpiredIdenfyKYCQuery, useSendManualKYCEmailMutation, useSendManualKYBEmailMutation,
  useLazyGetExpiredKYBDocumentsQuery, useResetExpiredKYBDocumentsMutation,
  useGetUserDashboardQuery, usePostPaxumDataMutation,
  useLazyGetCountryPaymentsConfigQuery, useDeleteCountryPaymentConfigMutation, usePostCountryPaymentConfigMutation,
  useLazyGetBannedCountryPaymentsConfigQuery, useLazyGetCtoPMapsQuery, usePostEditCountryPaymentConfigMutation,
  useLazyGetCountryPaymentsConfigHistoryQuery, useLazyRunGetQuery,
  useUploadDacExcelMutation, useLazyGetDAC7TokenQuery, useLazyCheckDAC7TokenQuery, useLazyGetDAC7DataQuery,
  usePostDAC7SubmissionMutation, usePostAvailablePaymentConfigMutation, useLazyGetAvailablePaymentConfigsQuery,
  useSendDAC7EmailMutation, useGetDAC7ActivityQuery, useLazyGetDAC7ActivityQuery, useLazyGetAdBuysQuery,
  useLazyRunTest1Query, useLazySyncPaymentMMIQuery, useLazyGetKYBSubmissionJSONQuery,
  useLazyGetRemindersQuery, useInsertReminderMutation, useDeleteReminderMutation,
  useLazyGetIdenfyResponseQuery,
  useAdminLoginMsMutation,
  useLoadTechAuditQuery,
  useUpsertTechCategoryMutation,
  useUpsertTechEntryMutation,
  useAddTechTaskMutation,
  useUpdateTechTaskMutation,
  useDeleteTechTaskMutation,
  useDeleteTechCategoryMutation,
  useLoadClaudeAccessStatusQuery,
  useConfirmClaudeAccessMutation,
  useUploadClaudeCertMutation,
  useLoadCompanyDirectoryQuery,
  useLoadCompanyDirectoryFullQuery,
  useLoadAdvisorsQuery,
  useLoadMyReportsQuery,
  useLoadMyReportsUsageQuery,
  useRequestClaudeUsageMutation,
  useLoadAllClaudeSpendQuery,
  useGetCalendarAccessQuery,
  useGetEventsCalendarStatusQuery,
  useLazyGetEventsCalendarQuery,
  useGetEventsCalendarSettingsQuery,
  useSetEventsCalendarSettingsMutation,
  useUpsertCompanyDirectoryEntryMutation,
  useDeleteCompanyDirectoryEntryMutation,
  useSetDirectoryActiveMutation,
  useUploadCompanyDirectoryPhotoMutation,
  useListFirmsQuery,
  useAddFirmMutation,
  useDeleteFirmMutation,
  useLookupEmailHelpByLastNameMutation,
  useRequestEmailHelpResetCodeMutation,
  useVerifyAndResetEmailHelpPasswordMutation,
  useListClaudeSubmissionsQuery,
  useListB2BClaudeActivationsQuery,
  useListUnmatchedClaudeAccountsQuery,
  useUpdateClaudeSubmissionStatusMutation,
  useGetClaudeCertificateUrlMutation,
  useListAnnuityStatementsQuery,
  useListAnnuityPoliciesQuery,
  useUploadAnnuityStatementMutation,
  useGetAnnuityStatementUrlMutation,
  useDeleteAnnuityStatementMutation,
  useGetAllianzStatusQuery,
  useSyncAllianzMutation,
  useListJobOpeningsQuery,
  useCreateJobOpeningMutation,
  useUpdateJobOpeningMutation,
  useDeleteJobOpeningMutation,
  useUploadJobOpeningJdMutation,
  useGetJobOpeningJdUrlMutation,
  useDeleteJobOpeningJdMutation,
  useUploadJobOpeningVideoMutation,
  useGetJobOpeningVideoUrlMutation,
  useDeleteJobOpeningVideoMutation,
  useListJobNotifyRecipientsQuery,
  useAddJobNotifyRecipientMutation,
  useDeleteJobNotifyRecipientMutation,
  useNotifyNewJobPostingMutation,
  useListMyFormsQuery,
  useSubmitFormMutation,
  useListFormSubmissionsQuery,
  useGetRoleAlignmentResponsesQuery,
  useFetchRoleAlignmentResponsesMutation,
  useGetFormDashboardQuery,
  useSetFormManagerGatesMutation,
  useNotifyFormManagerMutation,
  useListFormCatalogQuery,
  useListFormNotifyRecipientsQuery,
  useAddFormNotifyRecipientMutation,
  useDeleteFormNotifyRecipientMutation,
  useSubmitJobApplicationMutation,
  useGetMyJobApplicationQuery,
  useListJobApplicationsQuery,
  useListManagedJobOpeningsQuery,
  useListActiveAnnouncementsQuery,
  useListAllAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useListHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
  useListSummerSchedulesQuery,
  useCreateSummerScheduleMutation,
  useUpdateSummerScheduleMutation,
  useDeleteSummerScheduleMutation,
  useLoadEmailMigrationQuery,
  useUpsertEmailMigrationMutation,
  useRefreshEmailMigrationPrimaryMutation,
  useListAiActivityQuery,
  useConfirmAiActivityMutation,
  useImpersonateMutation,
  useImpersonateReturnMutation,
  useListTechProjectsQuery,
  useCreateTechProjectMutation,
  useUpdateTechProjectStatusMutation,
  useDeleteTechProjectMutation,
  useListTechLanesQuery,
  useCreateTechLaneMutation,
  useSetTechLaneActiveMutation,
  useDeleteTechLaneMutation,
  useListTrainingCoursesQuery,
  useGetTrainingCourseQuery,
  useCreateTrainingCourseMutation,
  useUpdateTrainingCourseMutation,
  useSetTrainingCourseStatusMutation,
  useDeleteTrainingCourseMutation,
  useAddTrainingQuizModuleMutation,
  useAddTrainingVideoModuleMutation,
  useUpdateTrainingModuleMutation,
  useDeleteTrainingModuleMutation,
  useReorderTrainingModulesMutation,
  useGetTrainingVideoUrlMutation,
  useGetTrainingCourseResultsQuery,
  useListTrainingSandboxUsersQuery,
  useAddTrainingSandboxUserMutation,
  useRemoveTrainingSandboxUserMutation,
  useSubmitTrainingReviewMutation,
  useWithdrawTrainingReviewMutation,
  useApproveTrainingReviewMutation,
  useRejectTrainingReviewMutation,
  useListTrainingPendingReviewsQuery,
  useCountTrainingPendingReviewsQuery,
  useListMyTrainingCoursesQuery,
  useGetMyTrainingCourseQuery,
  useRecordVideoProgressMutation,
  useSubmitQuizAttemptMutation,
  useGetMyTrainingVideoUrlMutation,
  useHubspotSearchClientsQuery,
  useLazyHubspotSearchClientsQuery,
  useListClientUsersQuery,
  useCreateClientUserMutation,
  useCreateClientHouseholdMutation,
  useAddHouseholdMemberMutation,
  useGetClientUserQuery,
  useGetClientFscLinkQuery,
  useSetClientFscLinkMutation,
  useClearClientFscLinkMutation,
  useImpersonateClientUserMutation,
  useGetClientDocumentsReminderQuery,
  useSendClientDocumentsReminderMutation,
  useAddClientPointPersonMutation,
  useRemoveClientPointPersonMutation,
  useSetClientDocumentsMutation,
  usePublishClientUserMutation,
  useUnpublishClientUserMutation,
  useLinkClientHouseholdMutation,
  useCreateClientLoginMutation,
  useUpdateClientUserMutation,
  useDeleteClientUserMutation,
  useDeactivateClientUserMutation,
  useReactivateClientUserMutation,
  useGetClientUploadDownloadUrlMutation,
  useReExtractClientUploadMutation,
  useSetClientUploadOwnerMutation,
  useSetClientUploadYearMutation,
  useGetClientDocumentCatalogQuery,
  useGetClientSharedCatalogQuery,
  useListClientSharedDocumentsQuery,
  useShareClientDocumentMutation,
  useUpdateClientSharedDocumentMutation,
  useRevokeClientSharedDocumentMutation,
  useGetCrmCalendarSourcesQuery,
  useLazyGetCrmCalendarQuery,
  useGetCrmTaskLookupsQuery,
  useGetCrmTaskCountsQuery,
  useListCrmTasksQuery,
  useGetCrmTaskWorkloadQuery,
  useGetCrmTaskQuery,
  useCreateCrmTaskMutation,
  useUpdateCrmTaskMutation,
  useClaimCrmTaskMutation,
  useSaveCrmTaskQueueMutation,
  useSaveCrmTaskTypeMutation,
  useGetClientSharedDownloadUrlMutation,
  useGetClientSharedStreamUrlMutation,
  useStartSharedVideoMutation,
  useUploadSharedVideoPartMutation,
  useCompleteSharedVideoMutation,
  useAbortSharedVideoMutation,
  useGetClientSharedNotifyPreviewQuery,
  useSendClientSharedNotificationMutation,
  useListClientFirmsQuery,
  useListMyOfficesQuery,
  useSearchClientDocumentsQuery,
  useGetClientAgreementQuery,
  useSendClientAgreementMutation,
  useGetAdvisorSigningUrlMutation,
  useCreateSiteFeedbackMutation,
  useListSiteFeedbackQuery,
  useSetAdminStatusMutation,
  useSetAdminRolesMutation,
  useListEntrepreneurFirmsQuery,
  useCreateEntrepreneurFirmMutation,
  useListEntrepreneurUsersQuery,
  useCreateEntrepreneurUserMutation,
  useDeactivateEntrepreneurUserMutation,
  useSetEntrepreneurUserRoleMutation,
  useResetEntrepreneurUserPasswordMutation,
  useBulkUploadEntrepreneurUsersMutation,
  useListPendingPortalInvitesQuery,
  useSendPortalInvitesMutation,
  useListEntrepreneurApplicationsQuery,
  useApproveEntrepreneurFirmMutation,
  useRejectEntrepreneurFirmMutation,
  useVerifyEntrepreneurDomainMutation,
  useListEntrepreneurProvisionQueueQuery,
  useApproveEntrepreneurProvisionMutation,
  useRejectEntrepreneurProvisionMutation,
  useRequestEntrepreneurClaudeSeatMutation,
  useWithdrawEntrepreneurClaudeSeatMutation,
  useGetEntrepreneurClaudeCertUrlMutation,
  useGetEntrepreneurFeePreviewQuery,
  useListEntrepreneurActivationInvoicesQuery,
  useListEntrepreneurInvoicesQuery,
  useRunEntrepreneurBillingMutation,
  useGetEntrepreneurFirmBillingSummaryQuery,
  useGetEntrepreneurUsageChargesQuery,
  useChargeEntrepreneurUsageMutation,
  useSyncEntrepreneurStripeCustomersMutation,
  useGetEntrepreneurFirmBillingStatusQuery,
  useGetEntrepreneurFirmSetupIntentQuery,
  useSetEntrepreneurFirmPaymentMethodMutation,
  useRemoveEntrepreneurFirmPaymentMethodMutation,
  useRefundEntrepreneurInvoiceMutation,
  useGetEntrepreneurAnnualSeatFeeQuery,
  useSetEntrepreneurAnnualSeatFeeMutation,
  useGetEntrepreneurMaxUserLimitQuery,
  useSetEntrepreneurMaxUserLimitMutation,
  useSearchEntrepreneurActionsLogQuery,
  useImpersonateEntrepreneurUserMutation,
  useGetEntrepreneurNotificationsQuery,
  useSetEntrepreneurNotificationsMutation,
  useGetFscTrainingConfigQuery,
  useSetFscTrainingConfigMutation,
  useListFscTrainingSignupsQuery,
  useListB2BAnnouncementsQuery,
  useCreateB2BAnnouncementMutation,
  useUpdateB2BAnnouncementMutation,
  useDeleteB2BAnnouncementMutation,
  useListTransitionsQuery,
  useSetFirmStageMutation,
  useGetTransitionQuery,
  useUpdateTransitionPlanMutation,
  useSaveTransitionItemMutation,
  useDeleteTransitionItemMutation,
  useReorderTransitionItemsMutation,
  useListRegSpSoftwareQuery,
  useGetRegSpSoftwareQuery,
  useCreateRegSpSoftwareMutation,
  useUpdateRegSpSoftwareMutation,
  useDeleteRegSpSoftwareMutation,
  useSignoffRegSpSoftwareMutation,
  useGetRegSpAuditQuery,
  useAddRegSpLinkMutation,
  useDeleteRegSpLinkMutation,
  useUploadRegSpDocumentMutation,
  useGetRegSpDocumentUrlMutation,
  useDeleteRegSpDocumentMutation,
  useSearchFscHouseholdsQuery,
  useListFscFirmsQuery,
  useListFscRepsQuery,
  useGetFscHouseholdQuery,
  useLazyGetFscHouseholdQuery,
  useListFscHouseholdDocumentsQuery,
  useGetFscSyncStatusQuery,
  useSearchCrmClientsQuery,
  useGetCrmSummaryQuery,
  useGetCrmOptionsQuery,
  useAskCrmMutation,
  useGetCrmClientQuery,
  useLazyGetCrmClientQuery,
  useListAgenticWmThreadsQuery,
  useGetAgenticWmThreadQuery,
  useOpenAgenticWmThreadMutation,
  useRenameAgenticWmThreadMutation,
  useArchiveAgenticWmThreadMutation,
  useSetAgenticWmFeedbackMutation,
  useGetAgenticWmUsageQuery,
  useListAgenticWmAllThreadsQuery,
  useLazyGetAgenticWmReviewThreadQuery,
  useListAgenticWmFeedbackQuery,
  useListAgenticWmViewsQuery,
  useGetAumSummaryQuery,
  useGetAumTrendQuery,
  useGetAumPlatformQuery,
  useGetAumStylesQuery,
  useGetAumFirmAdvisorsQuery,
  useListAumFirmsQuery,
  useGetAumStatusQuery,
  useGetAumHouseholdsQuery,
  useLazyGetAumHouseholdsCsvQuery,
  useGetOrionFlowTypesQuery,
  useSetOrionFlowTypesMutation,
  useGetAumHouseholdOrionQuery,
  useGetAumNotInFscQuery,
  useGetAumBreakdownQuery,
  useGetMigratedDocumentUrlMutation,
  useGetCurrentComplianceBulletinQuery,
  useListComplianceBulletinsQuery,
  useGetComplianceBulletinContentMutation,
  useUploadComplianceBulletinMutation,
  useUpdateComplianceBulletinMutation,
  useDeleteComplianceBulletinMutation,
  useGetMarketingLibraryQuery,
  useGetMarketingResourceUrlMutation,
  useOpenMarketingResourceMutation,
  useOpenMarketingGroupMutation,
  useGetMarketingActivitySummaryQuery,
  useGetMarketingActivityFirmsQuery,
  useGetMarketingActivityMembersQuery,
  useGetMarketingActivityItemsQuery,
  useLazyGetMarketingActivityCsvQuery,
  useGetMarketingAdminLibraryQuery,
  useCreateMarketingCategoryMutation,
  useUpdateMarketingCategoryMutation,
  useDeleteMarketingCategoryMutation,
  useReorderMarketingCategoriesMutation,
  useCreateMarketingGroupMutation,
  useUpdateMarketingGroupMutation,
  useDeleteMarketingGroupMutation,
  useReorderMarketingGroupsMutation,
  useCreateMarketingLinkMutation,
  useCreateMarketingHeadingMutation,
  useGetMarketingUploadTicketMutation,
  useCommitMarketingUploadMutation,
  useUpdateMarketingResourceMutation,
  useDeleteMarketingResourceMutation,
  useReorderMarketingResourcesMutation,
  // Resource Library — the employee reader plus the MARKETING authoring page.
  useGetResourcePagesQuery,
  useGetResourceItemFileUrlMutation,
  useGetResourceAdminPagesQuery,
  useCreateResourceGroupMutation,
  useUpdateResourceGroupMutation,
  useDeleteResourceGroupMutation,
  useReorderResourceGroupsMutation,
  useCreateResourcePageMutation,
  useUpdateResourcePageMutation,
  useDeleteResourcePageMutation,
  useReorderResourcePagesMutation,
  useCreateResourceLinkMutation,
  useGetResourceUploadTicketMutation,
  useCommitResourceUploadMutation,
  useUpdateResourceItemMutation,
  useDeleteResourceItemMutation,
  useReorderResourceItemsMutation,
  useGetMyFormSubmissionQuery,
  useGetFormDraftQuery,
  useSaveFormDraftMutation,
  useDeleteFormDraftMutation,
  useGetFormsTeamStatusQuery,
  useLazyGetReportRoleAlignmentQuery,
  useSubmitManagerialAssessmentMutation,
  useLazyGetMeetingsAgendaQuery,
  useBuildMeetingBriefMutation,
  useLazyGetPlannerSummaryQuery,
  useLazyGetMailFoldersQuery,
  useMoveMessageMutation,
  useAskRoleAlignmentMutation,
  useDismissBriefingMessageMutation,
  useUndismissBriefingMessageMutation,
  useLazyGetTaskSourcesQuery,
  useLazyGetTaskItemsQuery,
  useLazyResolveTaskPeopleQuery,
  useCreateTaskMutation,
  useSetTaskCompletedMutation,
  useLazyGetYourDayBriefingQuery,
  useLazyGetYourDayWeekReviewQuery,
  useLazyGetYourDayMailboxesQuery,
  useScanYourDayMailboxesMutation,
  useAddYourDayMailboxMutation,
  useRemoveYourDayMailboxMutation,
  useGetSlackStatusQuery,
  useLazyGetSlackStatusQuery,
  useConnectSlackMutation,
  useDisconnectSlackMutation,
  useAskGlanceMutation,
  useGetMyPreferencesQuery,
  useSetMyTimezoneMutation,
  useLazyGetCalendarsQuery,
  useLazyGetCalendarEventsQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
  useInviteCalendarAttendeesMutation
 } = api;
